import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { parseCsv } from "../../utils/csv";
import { useQueryClient } from "@tanstack/react-query";
import "./module.css";
import "../backlog/backlog.css";
import { useModuleList, fetchModuleRows } from "./queries";
import { ColumnFilter } from "./ColumnFilter";
import { effectiveDateRange } from "./moduleDateRange";
import { QueryCombo } from "./QueryCombo";
import { QueryDatePicker } from "./QueryDatePicker";
import { DateRangeSelect, TRACKING_RANGES } from "./DateRangeSelect";
import { MODULE_CRUD, type CrudField, type CrudListAction } from "./moduleCrud";
import { ListActionChoiceDialog } from "./ListActionChoiceDialog";
import { fitPct, fitTotal } from "./fitColumns";
import { runLoading, LoaderSymbol, BusyIcon } from "../../components/loading";
import { SettingsDetailPage, type DetailDataAdapter } from "./SettingsDetailPage";
import { ModuleCrudDialog } from "./ModuleCrudDialog";
import { OpenProspectDialog } from "./OpenProspectDialog";
import { ImportFileDialog } from "./ImportFileDialog";
import { ParameterPickerDialog } from "./ParameterPickerDialog";
import { QuickAddDialog } from "./QuickAddDialog";
import { AiCombo } from "../../components/AiCombo";
import { invalidatePreviews, prefetchGet, prefetchPreview } from "./previewCache";
import { MsrDetailDialog } from "./MsrDetailDialog";
import { SampleFieldConfigDetail } from "./SampleFieldConfigDetail";
import { SampleMatrixFieldSetupDetail } from "./SampleMatrixFieldSetupDetail";
import { FlutterTemplateBuilderDetail } from "./FlutterTemplateBuilderDetail";
import { MdiTemplatesDetail } from "./MdiTemplatesDetail";
import { MdiLogDetail } from "./MdiLogDetail";
import DailyQcBuilderPage from "../settings/dailyQcBuilder/DailyQcBuilderPage";
import { DefaultPricingNewPage } from "../settings/DefaultPricingNewPage";
import { AssignItemsDialog } from "./AssignItemsDialog";
import { StockQtyEditDialog } from "./StockQtyEditDialog";
import { InstrumentPickerDialog } from "./InstrumentPickerDialog";
import { ReportRecipientsDialog, type RecipientKind } from "./ReportRecipientsDialog";
import { MsrDataImportConfigDetail } from "./MsrDataImportConfigDetail";
import { MsrSummaryDialog } from "./MsrSummaryDialog";
import { MsrAutoGenerateDialog } from "./MsrAutoGenerateDialog";
import { RelatedLookups } from "./RelatedLookups";
import { AppToolbar, ExportMenu, type ExportData } from "../../components/AppToolbar";
import { TestCodeCopyFromPanel } from "./TestCodeCopyFromPanel";
import { TestCodeRecordActions } from "./TestCodeRecordActions";
import { ProspectCloseButton } from "./ProspectCloseButton";
import { TestCodeSetupStrip } from "./TestCodeDetailTabs";
import { TestCodeCopyGroupDialog } from "./TestCodeCopyGroupDialog";
import {
  TestCodeCategoryChips,
  TestCodeSetCategoryDialog,
  useTestCodeCategories,
} from "./TestCodeCategoryBar";
import { useHotkeys, formatCombo, effectiveCombo, type HotkeyDef } from "../../components/hotkeys";
import { downloadBlob, saveBlobAs } from "../../utils/exportData";
import { api, runWithAuditReason } from "../../api/client";
import { fuzzyFilter } from "../../utils/fuzzyMatch";
import { boClassOf } from "../../layouts/boClass";
import { consumePendingRecord } from "../../layouts/navigateTo";
import { statusVariant } from "../../components/statusPill";
import { isUtilityForm, pageGroupId, navSubgroupPath } from "../../layouts/navData";
import { useGridLayoutDefaults, moduleColumnWidth } from "../../components/AppDataGrid/gridLayoutContext";
import { PREVIEW_AS_TOKEN, previewTicketOf, previewUrl } from "../../utils/reportPreview";
import { useReasonGate, useConfirm } from "../../components/confirm";
import { FormTitle } from "../../components/FormTitle";
// Global grid features (Sort · …) — the SAME header right-click menu AppDataGrid
// uses, wired here via a tiny adapter over this grid's own sortCol/sortDesc state
// (client 2026-08-03: "all the grids, list AND detail view — global requirement").
import {
  useHeaderMenu,
  useColumnManager,
  loadGridPrefs,
  saveGridPrefs,
  type HeaderMenuSort,
} from "../../components/AppDataGrid";
import { StepHint } from "../../components/StepHint/StepHint";
import { ViewToggle } from "../../components/ViewToggle";
import { useNotify } from "../../components/notifications";
import { useNavActions } from "../../hooks/useNavActions";
import { confirmDiscardIfUnsaved } from "../../hooks/useUnsavedGuard";
import { fmtLocalDateTime, fmtLocalDate } from "../../utils/datetime";
import {
  PAGE_SIZES,
  DEFAULT_PAGE_SIZE,
  pageSizeLabel,
  MODULE_DEFAULT_RANGE_DAYS,
  wirePageSize,
} from "../../components/listDefaults";

const RANGES: { label: string; days: number | null }[] = [
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "2Y", days: 730 },
  { label: "5Y", days: 1825 },
  { label: "10Y", days: 3650 },
  { label: "All", days: null },
];
// PAGE_SIZES + pageSizeLabel sourced from shared listDefaults.ts.
const ISO = /^\d{4}-\d{2}-\d{2}T/;

// UI-8: ISO/date string → yyyy-MM-dd for an inline <input type="date">.
function toDateInputLocal(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Modules that get the full "Query Samples" dialog + Query button.
// Result Samples + Historic Results + Monthly Summary Repository (each shows
// only its own grid columns — see QUERY_FIELDS_BY_MODULE / QUERY_DATES_BY_MODULE).
/** Width of the leading row-select checkbox column. */
const SELECT_COL_W = 40;
/** Read-only ModuleListView lists that STILL show a leading selection checkbox column (no CRUD
 *  actions) — a legacy multi-select list with nothing to edit. Owner request 2026-10-26 (CRM ▸ Agenda).
 *  (Closed Clients gets its checkbox from its own detailReadOnly MODULE_CRUD entry instead.) */
const SELECTABLE_LIST_KEYS = new Set<string>(["crm-agenda", "crm-unfollowed-prospects"]);
/** Width of the leading per-row Edit-pencil column. */
const PENCIL_COL_W = 44;
/** Width assumed for a fitAllColumns column that declares none, so the percentages
 *  still add up to the whole table. */
const FIT_FALLBACK_COL_W = 120;

const ADVANCED_FILTER_MODULES = new Set(["dc2-resultsamples", "histres", "msr"]);

// Modules that get the enhanced toolbar: custom orange Date Range dropdown,
// 3M default, Retrieve-gated range (no auto-fetch), and colored Retrieve/Export
// buttons. Superset of ADVANCED_FILTER_MODULES.
const ENHANCED_TOOLBAR_MODULES = new Set(["dc2-resultsamples", "histres"]);

// NOTE (2026-09-26): there are no longer any popup-overlay bespoke-detail
// modules. 'msr-data-import-config' (five multi-select GUID pickers + ImportMode
// radio) was the last one; it now opens a FULL-PAGE inline detail
// (<MsrDataImportConfigDetail>) in the early-return branch below, like 'msr'
// (<MsrDetailDialog inline>) and the Sample Field Config details — matching the
// no-popup standard (standing rule 12). Any CRUD detail that can't be a
// MODULE_CRUD field list is dispatched by moduleKey inside that branch.

// CRUD modules whose full-page detail is rendered by a BESPOKE inline component
// (still List/Detail tab + full-page, just not the generic SettingsDetailPage).

// "Query Samples" dialog — the text fields (ALPACA layout), each mapped to a
// Result Samples column. Only fields that actually filter this grid are shown.
const QUERY_FIELDS: { key: string; label: string }[] = [
  { key: "projectId", label: "Project ID" },
  { key: "siteName", label: "Site" },
  { key: "client", label: "Client" },
  { key: "jobId", label: "Job ID" },
  { key: "testName", label: "Test" },
  { key: "test", label: "Method" },
  { key: "parameter", label: "Parameter" },
];

// Date fields in the "Query Samples" dialog — each a From/To pair backed by
// its own server params. Received reuses the toolbar's fromDate/toDate.
const QUERY_DATES = [
  { key: "collected", label: "Date Collected" },
  { key: "received", label: "Date Received" },
  { key: "analyzed", label: "Date Analyzed" },
];

// Per-module Query Panel layout — each entry lists ONLY the combos/date ranges
// that exist as columns on that module's grid (a combo's `key` must be a real
// column so its distinct list resolves).  Modules not listed here fall back to
// the full Result Samples layout (QUERY_FIELDS / QUERY_DATES).
const QUERY_FIELDS_BY_MODULE: Record<string, { key: string; label: string }[]> = {
  "dc2-resultsamples": QUERY_FIELDS,
  // Historic Results — its grid has projectId / siteName / client / jobId /
  // method / parameter (no separate Test column; Method is keyed 'method').
  histres: [
    { key: "projectId", label: "Project ID" },
    { key: "siteName", label: "Site" },
    { key: "client", label: "Client" },
    { key: "jobId", label: "Job ID" },
    { key: "method", label: "Method" },
    { key: "parameter", label: "Parameter" },
  ],
  // Monthly Summary Repository — match GoLive's Query dialog exactly: Year, Month, MSRID.
  msr: [
    { key: "year", label: "Year" },
    { key: "month", label: "Month" },
    { key: "msrId", label: "MSRID" },
  ],
};
const QUERY_DATES_BY_MODULE: Record<string, { key: string; label: string }[]> = {
  "dc2-resultsamples": QUERY_DATES,
  // Historic Results grid shows Date Received + Analysis Date (no Date Collected).
  histres: [
    { key: "received", label: "Date Received" },
    { key: "analyzed", label: "Date Analyzed" },
  ],
  // MSR — no Query-panel date pickers; the toolbar Date Range covers CreatedDate.
  msr: [],
};

// Forms rendered as a collapsible grouped grid (GoLive parity).  Maps the
// module key → { col: column to group by, prefix: header label prefix }.
// The grouped column becomes the "{prefix}{value} (Count=N)" header; the
// remaining columns show as detail rows.
const GROUPED_BY: Record<string, { col: string; prefix: string }> = {
  "dc2-userroleright": { col: "role", prefix: "Role: " },
  "dc2-userpermission": { col: "groupKey", prefix: "" },
};

function fmt(v: unknown, dateOnly = false, currency = false): string {
  if (v === null || v === undefined) return "";
  // A money column (ModuleColumn.currency) shows G5's currency format, $1,050.00. Display only:
  // the row keeps the number, so sorting and filtering stay numeric.
  if (currency && v !== "" && Number.isFinite(Number(v))) {
    return "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  // Dates render in the viewer's LOCAL timezone, 24-hour, no AM/PM (CLAUDE.md
  // §14b.6).  fmtLocal force-UTCs the wire string; fall back to raw if unparseable.
  // A date-only column (ModuleColumn.dateOnly) drops the time via fmtLocalDate.
  if (typeof v === "string" && ISO.test(v)) return (dateOnly ? fmtLocalDate(v) : fmtLocalDateTime(v)) || v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

// Columns whose value domain is a TEXT enum that happens to include 'Yes'/'No'
// (e.g. Data Package Review Checklist's Default = Yes / No / N/A). These must
// render the literal word — NOT the green ✓/✗ checkbox used for true boolean
// columns — matching the legacy ALPACA list. Keyed by ModuleColumn.key.
// (user report 2026-08-26; 'pickAnswer' = Check Points Setup PickAnswer enum
// Yes/No/N/A, 2026-09-12)
const FORCE_TEXT_COLS: ReadonlySet<string> = new Set(["defaultOpt", "pickAnswer"]);

/** Lifecycle-status words a grid cell may hold, and the class that colours them.
 *  GOOD (green): the row is live / in force. BAD (red): it is not. NEUTRAL
 *  (muted): valid but not yet in force.
 *
 *  Returns null for any other value, so ordinary cells are untouched — a cell
 *  only becomes coloured by holding one of these exact words.
 *
 *  Every returned class contains "status" ON PURPOSE: theme/blackText.css forces
 *  `color: var(--ai-ink) !important` on everything inside a td except classes
 *  matching [class*="status" i] / pill / chip / badge. Rename these and the
 *  colour silently reverts to black. */
const STATUS_GOOD: ReadonlySet<string> = new Set(["Active", "Current"]);
const STATUS_BAD: ReadonlySet<string> = new Set(["Inactive", "Expired", "Retired"]);
// Not-in-force states — muted, so neither reads as good or bad. 'Pending' is not
// in force YET; 'Renewed' no longer is (a successor has taken over). Muting both
// keeps green meaning exactly one thing: this is the row in force today.
const STATUS_NEUTRAL: ReadonlySet<string> = new Set(["Pending", "Renewed"]);
// Mid-workflow — amber, the app-wide "needs attention" colour (matches
// statusPill's 'in progress' rule so a permit renewal reads like every other
// in-flight state in the product).
const STATUS_BUSY: ReadonlySet<string> = new Set(["Renewal In Progress", "Renewal Due"]);

/** Scheduled-but-not-yet-effective limits carry the DATE in the value
 *  ('Future — from 11/01/2027'), so they cannot be matched by an exact word the
 *  way the sets above are — hence a prefix test. Emitted by BOTH the consolidated
 *  Permit Limits list (ModuleRegistry) and PermitSetupReader; the em dash is what
 *  they both write, but a plain hyphen is accepted too so a future edit to either
 *  SQL string cannot silently turn the colour off. */
const STATUS_FUTURE = /^Future\s*[\u2014-]\s*from\b/;

/** Operator KIND → badge colour, for the Operators reference list.
 *  Deliberately NOT folded into statusClass(): these are taxonomy labels, not
 *  lifecycle states, and colouring every cell in the app that happens to read
 *  "Range" would be wrong. */
const OPERATOR_KIND_CLASS: Readonly<Record<string, string>> = {
  Numeric: "numeric", // blue  — compares magnitudes
  Range: "range", // green — needs Limit1 AND Limit2
  Qualitative: "qualitative", // violet — compares TEXT (Present / Absent)
};

function statusClass(v: string): string | null {
  if (STATUS_GOOD.has(v)) return "mlv-status on";
  if (STATUS_BAD.has(v)) return "mlv-status";
  if (STATUS_NEUTRAL.has(v)) return "mlv-status pending";
  if (STATUS_BUSY.has(v)) return "mlv-status busy";
  if (STATUS_FUTURE.test(v)) return "mlv-status future";
  return null;
}

// ID columns render in the brand dark-orange (CSS `.mlv-id`, var --id-color)
// so the eye lands on the record key first.  Convention-driven so every
// ALPACA list form gets it for free: a column is an ID when its key ends in
// `id`/`Id`/`ID` (jobId, sampleId, reportId, oid, macId, …) OR its label is /
// ends with "ID"/"No"/"Number" (Job ID, MAC ID, Report No, User Number).
// Pure helper — same input, same output, no side effects.
const ID_KEY = /(^|[a-z])(id|oid)$/i;

/** Lists whose ModuleRegistry widths ARE the G5 ListView widths, copied
 *  verbatim from the legacy model — they keep them instead of a module-wide
 *  column width (Data Center's 140px). Client Contacts, 2026-10-13. */
const G5_WIDTH_MODULES = new Set(["dc-contact"]);

/** Data Center lists that do NOT default to a first-column sort — Result
 *  Samples' fast UNION path (ModuleRegistry FastUnion) sorts on its indexed
 *  column only, so it keeps that order. */
const NO_FIRST_COL_DEFAULT = new Set(["dc2-resultsamples"]);

/** Data Center lists whose first-column default is DESCENDING — G5 sorts
 *  Custom Reports newest ReportID first. Mirrors ModuleListReader
 *  .FirstColumnDescending (server side). */
const FIRST_COL_DESC = new Set(["dc-reporting"]);
const ID_LABEL = /\b(id|no|number)$/i;
// eslint-disable-next-line react-refresh/only-export-components
export function isIdColumn(key: string, label: string): boolean {
  return ID_KEY.test(key) || ID_LABEL.test(label.trim());
}

/**
 * Legacy dashboard TABS over one list: each tab pins a column filter, so the grid,
 * its count, its paging and its export all describe that tab only. Legacy
 * "Scientific Data Table" is a dashboard of two list views over the same table —
 * Raw Data ([ResultType] = 'RawDataTable') and Calibration ('CalibrationTable');
 * without the tabs the list showed every ResultType at once (372 rows against
 * legacy's 318 on Raw Data), including LDAR rows legacy shows on neither tab.
 * Keyed by module, so no other list changes.
 */
const LIST_SCOPE_TABS: Record<string, { column: string; tabs: { label: string; value: string }[] }> = {
  "set-sdms-scientificdata": {
    column: "resultType",
    tabs: [
      { label: "Raw Data", value: "RawDataTable" },
      { label: "Calibration", value: "CalibrationTable" },
    ],
  },
};

interface Props {
  moduleKey: string;
  group: string;
  onHome: () => void;
  navTitle?: string;
  /** Extra form-specific toolbar button(s) rendered at the START of the shared
   *  AppToolbar's left slot (before the config-driven listActions / Refresh) — e.g.
   *  LimitsPage injects its "Import Limits" action here so it rides the one toolbar. */
  toolbarExtras?: ReactNode;
  /** Extra button(s) placed right AFTER Refresh and before Export to (G5 order) — e.g. Internal Audit ▸ Correction
   *  Verification's History switch. */
  toolbarExtrasAfterRefresh?: ReactNode;
}

// ── MSR sample export (ALPACA parity 2026-07-10) ────────────────────────────
// Matches SelectAndExportMSRSamplesInxlsx_Execute / …InCsv_Execute in legacy.
// Fetches saved sample rows for every selected MSR Oid, combines them, and
// writes an XLSX or CSV with ALPACA's exact column headers.
// GoLive's exact 17-column export layout. The date header carries a TRAILING
// SPACE and the last column is SampleCollector — the client's program that
// consumes this file matches that layout, so both are reproduced verbatim
// (fix 2026-08-13: AI was exporting only 16 columns, no SampleCollector, which
// crashed the client program).
const MSR_SAMPLE_HEADERS = [
  "Run",
  "SiteID",
  "Address",
  "KeyMap",
  "Sample Collected Date & Time ",
  "FreeChlorine (mg/L)",
  "TotalChlorine (mg/L)",
  "MonoChloramine (mg/L Cl2)",
  "FreeAmmonia (mg/L NH3-N)",
  "TotalColiform",
  "E.coli",
  "Nitrite (mg/L)",
  "Nitrate (mg/L)",
  "SampleType",
  "Disinfectant",
  "Comments",
  "SampleCollector",
];
// GoLive column widths — one entry per header (17).
const MSR_COL_WIDTHS = [
  6.29, 15.29, 34.43, 8.29, 28.29, 18.86, 19.29, 26, 26.14, 13.14, 11.86, 13.14, 13.57, 11.86, 11.71, 10.57, 16,
];

interface MsrSampleRow {
  projectId?: string;
  siteName?: string;
  address?: string;
  keyMap?: string;
  date?: string;
  time?: string;
  freeChlorine?: string;
  totalChlorine?: string;
  monochloramine?: string;
  freeAmmonia?: string;
  totalColiform?: string;
  ecoli?: string;
  nitrite?: string;
  nitrate?: string;
  sampleType?: string;
  disinfectant?: string;
  comment?: string;
  collector?: string;
}

// GoLive "Sample Collected Date & Time" format: MM/dd/yyyy hh:mm tt — zero-padded
// 12-hour + AM/PM (e.g. "08/04/2026 09:33 AM"). This is an EXTERNAL machine-read
// export consumed by the client's program, so it matches GoLive EXACTLY — NOT the
// app-wide 24-hour UI rule. AI was emitting "yyyy/MM/dd HH:mm", which the client
// program could not parse (fix 2026-08-13).
function msrFormatDateTime(date?: string, time?: string): string {
  if (!date) return "";
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  const datePart = d ? `${d[2]}/${d[3]}/${d[1]}` : date;
  const t = time ? /^(\d{1,2}):(\d{2})/.exec(time) : null;
  let timePart = "";
  if (t) {
    const h = parseInt(t[1] ?? "0", 10);
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    timePart = `${String(h12).padStart(2, "0")}:${t[2] ?? ""} ${ampm}`;
  }
  return [datePart, timePart].filter(Boolean).join(" ");
}

function msrSampleToRow(s: MsrSampleRow): string[] {
  return [
    s.projectId ?? "",
    s.siteName ?? "",
    s.address ?? "",
    s.keyMap ?? "",
    msrFormatDateTime(s.date, s.time),
    s.freeChlorine ?? "",
    s.totalChlorine ?? "",
    s.monochloramine ?? "",
    s.freeAmmonia ?? "",
    s.totalColiform ?? "",
    s.ecoli ?? "",
    s.nitrite ?? "",
    s.nitrate ?? "",
    s.sampleType ?? "",
    s.disinfectant ?? "",
    s.comment ?? "",
    s.collector ?? "",
  ];
}

function msrTimestamp(): string {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${n.getFullYear()}${pad(n.getMonth() + 1)}${pad(n.getDate())}${pad(n.getHours())}${pad(n.getMinutes())}${pad(n.getSeconds())}`;
}

/**
 * MSR "Export To XLSX / CSV".
 *
 * Two PHASES, and they must not overlap: the file is fetched + built under the
 * BLOCKING loader, then the loader is released BEFORE the shared "File Ready to
 * Save" dialog opens. Wrapping the save in the loader put the blocking scrim on
 * top of the dialog, so "Save As" could not be clicked and the spinner hung
 * forever (fix 2026-08-14) — hence runLoading lives here, not at the call site.
 */
async function msrExportSamples(
  kind: "xlsx" | "csv",
  selected: Set<string>,
  notify: ReturnType<typeof import("../../components/notifications").useNotify>,
): Promise<void> {
  const filename = `MSRSamples_${msrTimestamp()}`;
  let built: { blob: Blob; name: string } | null = null;

  // ── Phase 1 — fetch the rows + build the file (blocking loader) ────────────
  try {
    built = await runLoading(
      (async () => {
        const allRows: string[][] = [];
        for (const oid of [...selected]) {
          const resp = await api.get<{ success: boolean; data: MsrSampleRow[] }>(
            `/api/v1/monthly-summary-repository/${oid}/samples`,
          );
          allRows.push(...(resp.data?.data ?? []).map(msrSampleToRow));
        }
        if (allRows.length === 0) return null;

        if (kind === "xlsx") {
          // GoLive-matching XLSX: sheet "Data", bold header, font 11, freeze row 1 + col 1, exact column widths.
          const ExcelJS = (await import("exceljs")).default;
          const wb = new ExcelJS.Workbook();
          const ws = wb.addWorksheet("Data");
          ws.addRow(MSR_SAMPLE_HEADERS);
          for (const row of allRows) ws.addRow(row);
          ws.eachRow((row) => {
            row.font = { size: 11 };
            row.eachCell((cell) => {
              cell.font = { size: 11 };
            });
          });
          ws.getRow(1).eachCell((cell) => {
            cell.font = { bold: true, size: 11 };
          });
          ws.views = [{ state: "frozen", xSplit: 1, ySplit: 1, topLeftCell: "B2", activeCell: "B2" }];
          MSR_COL_WIDTHS.forEach((w, i) => {
            ws.getColumn(i + 1).width = w;
          });
          const buf = await wb.xlsx.writeBuffer();
          return {
            blob: new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            name: `${filename}.xlsx`,
          };
        }
        const csvCell = (s: string) => `"${String(s ?? "").replace(/"/g, '""')}"`;
        const csv = [MSR_SAMPLE_HEADERS, ...allRows].map((r) => r.map(csvCell).join(",")).join("\r\n");
        return {
          blob: new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }),
          name: `${filename}.csv`,
        };
      })(),
      { blocking: true, label: `Exporting to ${kind.toUpperCase()}…` },
    );
  } catch {
    notify.error(`Export to ${kind.toUpperCase()} failed. Please try again.`);
    return;
  }

  if (!built) {
    notify.info("No sample data found for the selected MSR(s).");
    return;
  }

  // ── Phase 2 — ask WHERE to save (no loader on screen: dialog is clickable) ──
  try {
    if (!(await saveBlobAs(built.blob, built.name))) return; // cancelled → no toast
    notify.success(`Exported to ${kind.toUpperCase()} successfully.`);
  } catch {
    notify.error(`Export to ${kind.toUpperCase()} failed. Please try again.`);
  }
}
// ────────────────────────────────────────────────────────────────────────────

/** The single shared form layout — every ALPACA module renders through here
 *  so all forms are identical (same control order), with internal x+y scroll
 *  and responsive sizing. */
export function ModuleListView({ moduleKey, group, onHome, navTitle, toolbarExtras, toolbarExtrasAfterRefresh }: Props) {
  const notify = useNotify();
  const gateReason = useReasonGate();
  const { confirm: confirmDlg } = useConfirm();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Test Code Category chips + the toolbar Set Category action. Harmless on
  // every other module: the chips render nothing without chip data, and the
  // category param is only read by SQL that mentions @Category.
  const isTestCode = moduleKey === "set-testcode";
  const [category, setCategory] = useState<string | null>(null);
  const [setCatOpen, setSetCatOpen] = useState(false);
  const testCodeCats = useTestCodeCategories();
  // Default Date Range mirrors each GoLive list-view's own default Date Filter
  // (e.g. ClientCollectors ships with "3M").  Keyed on moduleKey; the parent
  // remounts this component per module so the lazy initializer re-runs.
  // `rangeDays` is the DRAFT (what the Date Range dropdown shows). On advanced
  // forms the query uses `appliedRange`, which is only committed when the user
  // clicks Retrieve (or OK in the panel) — so picking a range doesn't auto-fetch.
  const initRange = ENHANCED_TOOLBAR_MODULES.has(moduleKey)
    ? 90 // enhanced forms (Result Samples, Historic Results) default to 3M
    : (moduleKey in MODULE_DEFAULT_RANGE_DAYS ? (MODULE_DEFAULT_RANGE_DAYS[moduleKey] ?? null) : 90); // ALPACA LIMS default is 3M (90d); null = All
  const [rangeDays, setRangeDays] = useState<number | null>(() => initRange);
  const [appliedRange, setAppliedRange] = useState<number | null>(() => initRange);
  // Enhanced toolbar (orange dropdown, Retrieve-gated, colored buttons).
  const enhanced = ENHANCED_TOOLBAR_MODULES.has(moduleKey);
  // Full "Query Samples" dialog (Result Samples + Historic Results).
  const advanced = ADVANCED_FILTER_MODULES.has(moduleKey);
  // The combos + date ranges this module's Query Panel shows (only its own
  // grid columns).  Falls back to the full Result Samples layout.
  const queryFields = QUERY_FIELDS_BY_MODULE[moduleKey] ?? QUERY_FIELDS;
  const queryDates = QUERY_DATES_BY_MODULE[moduleKey] ?? QUERY_DATES;
  // Balance the two-column panel: left = first N combos, right = the rest +
  // the date ranges (so each column holds ~half of all the rows).
  const queryLeftCount = Math.round((queryFields.length + queryDates.length) / 2);
  // Range actually used by the query: committed on enhanced forms, live elsewhere.
  const baseRange = enhanced ? appliedRange : rangeDays;
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Date Analyzed + Date Collected ranges (set by the "Query Samples" dialog).
  const [analysisFrom, setAnalysisFrom] = useState("");
  const [analysisTo, setAnalysisTo] = useState("");
  const [collectedFrom, setCollectedFrom] = useState("");
  const [collectedTo, setCollectedTo] = useState("");
  // Result Samples / Historic Results: an explicit Date Collected or Date Analyzed
  // search (Date Received left blank) widens the preset Date Range to All, so
  // historical rows — received long ago, e.g. the pre-Alpaca BTLIMSDC archive — are
  // NOT hidden by the received-date cutoff even though they match the
  // collected/analyzed filter. One lever: both the grid query and the column-filter
  // dropdowns read effectiveRange. (Result Samples historical-data ticket, 2026-10-29.)
  const effectiveRange = effectiveDateRange(baseRange, {
    receivedFrom: fromDate,
    receivedTo: toDate,
    collectedFrom,
    collectedTo,
    analyzedFrom: analysisFrom,
    analyzedTo: analysisTo,
  });
  // "Query Samples" centered modal: open flag + draft date fields + draft
  // multi-select picks (per column) for the checkbox combos.
  const [queryOpen, setQueryOpen] = useState(false);
  const [qForm, setQForm] = useState<Record<string, string>>({});
  const [qSel, setQSel] = useState<Record<string, string[]>>({});
  // Data Package Review Checklist — the row whose "View Test List" link is open.
  const [linkedTests, setLinkedTests] = useState<{ label: string; tests: string } | null>(null);
  // Items ▸ Stock Qty Edit — the ids ticked when the popup was opened (null = closed).
  const [stockQtyEditIds, setStockQtyEditIds] = useState<string[] | null>(null);
  // Assign Items To Test — the TestMethod row whose "Item" cell was double-clicked.
  const [assignItemsPopup, setAssignItemsPopup] = useState<{
    testMethodOid: string;
    testCode: string;
    testName: string;
  } | null>(null);
  // Instrument picker — opened by clicking the "(n) Instruments" cell in the
  // set-instruments-field / set-instruments-lab / set-instruments-sampleprep lists.
  const [instrumentPickerPopup, setInstrumentPickerPopup] = useState<{
    testMethodOid: string;
    testCode: string;
    testName: string;
    linkTable: string;
  } | null>(null);
  // Report Recipient View — the client row + kind whose "View Recipients" cell was clicked.
  const [recipientPopup, setRecipientPopup] = useState<{
    clientId: string;
    clientName: string;
    kind: RecipientKind;
    kindLabel: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  // Per-module initial page-size override (MODULE_CRUD.defaultPageSize) wins over
  // the global DEFAULT_PAGE_SIZE; falls back to the shared default otherwise.
  // …then the module default (Data Center = 100, from the shell's
  // GridLayoutProvider), then the app-wide default.
  const { pageSize: modulePageSize, defaultSortFirstColumn } = useGridLayoutDefaults();
  const [pageSize, setPageSize] = useState(MODULE_CRUD[moduleKey]?.defaultPageSize ?? modulePageSize ?? DEFAULT_PAGE_SIZE);
  // Per-column multi-select filters (ALPACA-style dropdowns).
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const scopeTabs = LIST_SCOPE_TABS[moduleKey];
  const [scopeTab, setScopeTab] = useState(0);
  // Click-to-sort on a column header (server-side, whitelisted to column keys).
  // Lazy-init from the saved per-module grid prefs so the sort restores on return.
  const savedSort = useMemo(() => loadGridPrefs(moduleKey).sort, [moduleKey]);
  const [sortCol, setSortCol] = useState<string | null>(savedSort?.key ?? null);
  const [sortDesc, setSortDesc] = useState<boolean>(savedSort?.dir === "desc");
  // Persist the sort whenever it changes (same store the column layout uses).
  useEffect(() => {
    saveGridPrefs(moduleKey, { sort: { key: sortCol, dir: sortCol ? (sortDesc ? "desc" : "asc") : null } });
  }, [moduleKey, sortCol, sortDesc]);
  const onSort = (key: string) => {
    if (shownSortCol === key) {
      // The first click on the default-sorted first column must flip it to
      // DESC, not re-request ASC — so pin the key as well as the direction.
      setSortCol(key);
      setSortDesc(!shownSortDesc);
    }
    else {
      setSortCol(key);
      setSortDesc(false);
    }
    setPage(1);
  };
  // Right-click header menu (Sort · Hide · Show All · Column Chooser) — adapt this
  // grid's own sort state to the shared <HeaderContextMenu>.  Clear Sort (key/dir
  // null) returns to the server's default order.  The menu is wired to the shared
  // column model (cm.columnMenu) LOWER DOWN — after `cols` (its source) is built —
  // so the hook call moved there; `headerMenu` is rendered once below (portals to body).
  const headerSort: HeaderMenuSort = {
    sort: { key: sortCol, dir: sortCol ? (sortDesc ? "desc" : "asc") : null },
    setSortExplicit: (key, dir) => {
      if (key === null || dir === null) {
        setSortCol(null);
        setSortDesc(false);
      } else {
        setSortCol(key);
        setSortDesc(dir === "desc");
      }
      setPage(1);
    },
  };

  // ── Opt-in CRUD (New / Edit / Delete / Audit) ───────────────────────────────
  // Only modules registered in MODULE_CRUD get write controls; every other
  // module renders exactly as before (read-only).  See moduleCrud.ts.
  // §2 — the BO class (boClass.ts) is the single source of truth. A Class E
  // (read-only) screen never gets a CRUD toolbar, even if MODULE_CRUD has an entry.
  // Every current CRUD module is A/B/C/F, so this changes nothing today; it's the
  // authoritative guardrail going forward (a dev warn flags any misclassification).
  const boClass = boClassOf(moduleKey);
  const crud = boClass === "E" ? undefined : MODULE_CRUD[moduleKey];
  // A read-only list opts into a leading selection checkbox (no CRUD) via SELECTABLE_LIST_KEYS;
  // selIdField is the row-id key (crud's when present, else the standard "oid").
  const selectableList = !crud && SELECTABLE_LIST_KEYS.has(moduleKey);
  const selIdField = crud?.idField ?? "oid";
  const hasSelectCol = (!!crud && !crud.pureListView) || selectableList;
  // Unfollowed Prospects (a selectable read-only list) gets a per-row Edit pencil that opens the
  // full "Open Prospect" form in EDIT mode (owner request 2026-10-26, image 989).
  const prospectsEditable = selectableList && moduleKey === "crm-unfollowed-prospects";
  const [prospectEditId, setProspectEditId] = useState<string | null>(null);
  if (import.meta.env.DEV && MODULE_CRUD[moduleKey] && boClass === "E") {
    // eslint-disable-next-line no-console
    console.warn(
      `[boClass] "${moduleKey}" defines MODULE_CRUD but is classified E (read-only) — classify it in layouts/boClass.ts.`,
    );
  }
  // A CRUD list UNDER SETTINGS shows the "Home › Settings › Form" breadcrumb ABOVE
  // the List/Detail tabs (owner decision 2026-10-14 — "All Settings forms only"),
  // matching the Exception Log page. Non-Settings CRUD lists show no breadcrumb and
  // keep the collapsed empty header (.mlv-hdr--empty); non-CRUD forms already render
  // the breadcrumb inside .dp-page-hdr (unchanged). pageGroupId maps a page id to its
  // top-level nav group, so this can't drift from the sidebar.
  const underSettings = pageGroupId[moduleKey] === "grp-settings";
  // Unfollowed Clients (CRM) also shows the breadcrumb above the List/Detail tabs
  // (owner request 2026-10-22): the only non-Settings CRUD list that opts in, so the
  // "Home › CRM › Unfollowed Clients" navigate control isn't lost once the view toggle
  // turns on (enabling crud otherwise suppresses the in-flow breadcrumb at line ~2539).
  // Contacts + Closed Clients (CRM) also show the breadcrumb above the List/Detail tabs
  // (owner request 2026-10-26), same opt-in as Unfollowed Clients — so the "Home › CRM › …"
  // navigate control isn't lost once the view toggle turns on.
  const CRM_CRUMB_KEYS = new Set(["crm-unfollowed-clients", "crm-contacts", "crm-clients-closed"]);
  // QA/QC ▸ Internal Audit (Complaint / Nonconformity — Initiation, Correction Verification, History)
  // opts in the same way (owner request 2026-10-27).
  const INTERNAL_AUDIT_CRUMB_KEYS = new Set(["qa-cc-init", "qa-cc-verif", "qa-cc-hist", "qa-nca-init", "qa-nca-verif", "qa-nca-hist"]);
  // Instrument Management ▸ Instruments opts in the same way (owner report 2026-11-02: the page showed an empty band above the
  // List/Detail switch where the breadcrumb belongs, unlike every Settings form).
  const INSTRUMENT_CRUMB_KEYS = new Set(["instm-labware"]);
  const showCrudCrumb =
    !!crud && (underSettings || CRM_CRUMB_KEYS.has(moduleKey) || INTERNAL_AUDIT_CRUMB_KEYS.has(moduleKey) || INSTRUMENT_CRUMB_KEYS.has(moduleKey)) && moduleKey !== "edi-limits";
  // MAC action permissions (New=create, Edit=write, Delete=delete). Computed for
  // the active module but CONSUMED ONLY in the MSR toolbar below, so no other
  // module-CRUD form's buttons change (Monthly Summary Repository is the sole
  // reporting form that routes through ModuleListView). Falls open for admins /
  // when no MAC level applies — only tightens where MAC data exists.
  const perm = useNavActions(moduleKey);
  const queryClient = useQueryClient();
  // New/Edit now opens the shared full-page Settings Detail View (BO UI Standard
  // §4 / CLAUDE.md §18.6) instead of a popup: `detail` holds { id } while the
  // detail is front (id === null = New), null = the list is front.
  // (Monthly Summary Repository special-case: `detail` opens the bespoke
  // <MsrDetailDialog> overlay instead of the generic SettingsDetailPage.)
  const [detail, setDetail] = useState<{ id: string | null; mode?: "view" | "edit" } | null>(null);
  /** Forces a fresh detail page. A record created with New is still id: null up
   *  here, so opening another New from it (Copy Test) would otherwise change
   *  nothing and leave the page on the saved record. */
  const [detailNonce, setDetailNonce] = useState(0);

  /* CROSS-FORM RECORD HAND-OFF (2026-09-30, owner: "Link these forms and those
     should be redirect to view the detail view").

     A <FormLink> on another form -- Item Initialization's Item / Storage /
     Department, Purchase Order's lookups -- navigates here with a record id.
     XAF sends a bound reference straight to that object's DetailView, so
     landing on the list and leaving the user to find the row again was the gap.
     Opened READ-ONLY, the same `mode: "view"` a row click produces, matching
     GoLive's `&mode=View`.

     `consumePendingRecord` CLEARS as it reads, so a later sidebar click on this
     same form lands on its list as standing rule 10 requires -- the hand-off is
     one-shot, not a sticky preference. Keyed on moduleKey (the parent remounts
     per module) and gated on `crud`, because a read-only registry list has no
     detail view to open. */
  useEffect(() => {
    if (!crud) return;
    const handoff = consumePendingRecord(moduleKey);
    if (handoff) setDetail({ id: handoff, mode: "view" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey]);
  const [summaryOpen, setSummaryOpen] = useState(false); // MSR "Summary" dialog
  const [autoGenOpen, setAutoGenOpen] = useState(false); // MSR "AutoGenerate" dialog
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Maximize/restore: hides the title + toolbar rows so the grid fills the page.
  const [fullGrid, setFullGrid] = useState<null | true>(null);
  // Prefix-invalidate every cached page of this module (queryKey ['module', key, params]).
  const refreshList = () => queryClient.invalidateQueries({ queryKey: ["module", moduleKey] });
  // Warm what the DETAIL view will need while the user is still reading the list.
  //
  //  - preview values (the next Permit ID), so New opens already filled in;
  //  - every lookup list the detail form's selects use. Those are small, static
  //    and were being re-fetched on EVERY open, which is most of why opening a
  //    row felt slow: one record GET plus three lookup GETs, each on the wire
  //    only once the form had already mounted.
  //
  // Cheap and idempotent - the cache short-circuits a fresh or in-flight entry.
  useEffect(() => {
    for (const f of crud?.fields ?? []) {
      if (f.previewUrl) prefetchPreview(f.previewUrl, f.key);
      // Cascading selects resolve their URL from a parent value, so they cannot
      // be warmed blind; independent lists can.
      if (f.optionsUrl && !f.dependsOn) prefetchGet(f.optionsUrl);
    }
  }, [crud]);

  // Escape key restores the grid from fullGrid mode.
  useEffect(() => {
    if (!fullGrid) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullGrid(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fullGrid]);

  // Hovering a row warms THAT record, so the click opens a form whose data is
  // already in hand instead of starting the fetch at click time.
  const prefetchRow = (row: Record<string, unknown>) => {
    if (!crud?.basePath) return;
    const rid = String(row[crud.idField] ?? "");
    if (rid) prefetchGet(`${crud.basePath}/${rid}`);
  };

  /** Test Code "Copy Test": the source the NEW form is pre-filled from.
   *  Cleared by a plain New, so the next blank form is actually blank. */
  const [copySource, setCopySource] = useState<
    {
      oid: string;
      matrix?: string;
      test?: string;
      /** The source is a group test: the panel copies MEMBERS (see CopyPrefill). */
      group?: boolean;
      defaults?: Record<string, unknown>;
    } | null
  >(null);
  const openNew = () => {
    setCopySource(null);
    setDetail({ id: null, mode: "edit" });
  };
  /** The header fields "Copy Test" carries from the source onto the NEW form.
   *  ONE list, used by both branches below, so a group copy and a normal copy
   *  can never disagree about what comes across.
   *
   *  Retire Date, Comment and the three holding times were added on owner
   *  report 2026-10-23 ("not copied when click the copy test"). The holding
   *  times are not columns on the test - they live on its dbo.TestGuide rows -
   *  but the form edits them here, so the copy has to seed them here too; the
   *  create then writes them to the rows the copy inserted. */
  const copyHeaderDefaults = (d: Record<string, unknown>): Record<string, unknown> => ({
    matrixId:     d.matrixId,
    methodId:     d.methodId,
    departmentId: d.departmentId,
    category:     d.category,
    retireDate:   toDateInputLocal(d.retireDate),
    comment:      d.comment,
    holdingTimeBeforePrepId:     d.holdingTimeBeforePrepId,
    holdingTimeBeforeAnalysisId: d.holdingTimeBeforeAnalysisId,
    setPrepTimeAsAnalysis:       d.setPrepTimeAsAnalysis,
  });

  /** Opens the NEW form already set to copy from `row` — the prototype's
   *  openNew(src). The header fields a copy almost always shares come across
   *  too, so the user types only the new Test Name. */
  const openNewCopiedFrom = async (rid: string) => {
    // Leaving the open record, so the standard unsaved-changes prompt applies.
    if (!(await confirmDiscardIfUnsaved())) return;
    try {
      const { data } = await api.get(`/api/v1/test-methods/${rid}`);
      const d = data?.data ?? {};
      /* A GROUP test copies MEMBERS, not parameter sections, and legacy asks for
         the source group in its own popup first (the "Copy Test Group" layout
         group, Model.xafml:69764, on Testparameter_DetailView_CopyTest). */
      if (d.isGroup) {
        setGroupCopy({ oid: rid, defaults: copyHeaderDefaults(d) });
        return;
      }
      setCopySource({
        oid: rid,
        matrix: d.matrixName ?? undefined,
        test: d.testName ?? undefined,
        defaults: copyHeaderDefaults(d),
      });
      setDetail({ id: null, mode: "edit" });
      setDetailNonce((n) => n + 1);
      notify.info(`New test pre-filled to copy from ${d.testName ?? "that test"}. Enter the new Test Name, then Save.`);
    } catch {
      notify.error("Could not read that test to copy from.");
    }
  };
  /** The "Copy Test Group" popup, open on a group test's Copy Test. */
  const [groupCopy, setGroupCopy] = useState<null | {
    oid: string;
    defaults: Record<string, unknown>;
  }>(null);

  /** OK on the Copy Test Group popup: open the NEW form with the chosen group
   *  as the copy source and the CURRENT test's header fields pre-filled, so the
   *  user types only the new Test Name — the same landing the normal Copy Test
   *  gives. */
  const takeGroupSource = (src: { oid: string; matrix: string; test: string }) => {
    // Is Group is ticked too: it is what puts the panel in group mode, and a copy
    // of a group test is a group test.
    setCopySource({ ...src, group: true, defaults: { ...groupCopy?.defaults, isGroup: true } });
    setGroupCopy(null);
    setDetail({ id: null, mode: "edit" });
    setDetailNonce((n) => n + 1);
    notify.info(`New group test pre-filled to copy the members of ${src.test}. Enter the new Test Name, then Save.`);
  };

  // Edit pencil / toolbar Edit → open the record EDITABLE.
  const openEdit = (row: Record<string, unknown>) => {
    if (!crud) return;
    setDetail({ id: String(row[crud.idField] ?? ""), mode: crud.detailReadOnly ? "view" : "edit" });
  };
  // Row click → open the record READ-ONLY (view), matching the legacy XAF flow
  // (row → DetailView to see the data, with an Edit button to switch).
  const openView = (row: Record<string, unknown>) => {
    if (!crud) return;
    setDetail({ id: String(row[crud.idField] ?? ""), mode: "view" });
  };
  // Toolbar "Edit" (canonical New · Edit · Delete) — edits the single checked
  // row via the same full-page detail; enabled only when exactly one is selected.
  const openEditSelected = () => {
    if (!crud || selected.size !== 1) return;
    setDetail({ id: Array.from(selected)[0]!, mode: crud.detailReadOnly ? "view" : "edit" });
  };

  // Canceled Item Charge ▸ Reactivate — un-cancels every ticked row, which returns it to the Item Charge
  // list. One POST per row, confirmed first, then the list refreshes.
  // A list action that must PICK a value first (crud.listActions with `choices`, e.g. Close
  // Prospects → Won / Cancelled) opens ListActionChoiceDialog; this holds the pending action.
  const [choiceAction, setChoiceAction] = useState<CrudListAction | null>(null);
  // Form-specific list actions over the ticked rows (crud.listActions — e.g. the
  // Technical Writer "Release"): shared confirm → one POST with every id → shared
  // success → refresh. With nothing ticked the legacy prompt is shown instead.
  const runListAction = async (action: CrudListAction) => {
    if (!crud?.basePath) return;
    const ids = Array.from(selected);
    if (ids.length === 0) {
      if (action.noSelectionMessage) notify.info(action.noSelectionMessage);
      return;
    }
    // A choice action (Close Prospects → Won / Cancelled) picks ONE value first, then POSTs it
    // through runChoiceAction — never the plain Yes/No confirm below.
    if (action.choices && action.choices.length > 0) { setChoiceAction(action); return; }
    const url = `${crud.basePath}/${action.path}${action.query ? `?${action.query}` : ""}`;
    // A file-returning action (e.g. Instrument ▸ PrintLabwareBarcode) changes nothing:
    // no confirm, download the file, keep the ticks so it can be printed again.
    if (action.download) {
      try {
        const res = await api.post<Blob>(url, { ids }, { responseType: "blob" });
        const cd = String((res.headers as Record<string, unknown>)?.["content-disposition"] ?? "");
        const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)?.[1];
        downloadBlob(res.data, name ? decodeURIComponent(name) : action.download);
        notify.success(action.successMessage);
      } catch (e) {
        // The error body arrives as a Blob because of responseType — read its message.
        const data = (e as { response?: { data?: unknown } })?.response?.data;
        let msg: string | undefined;
        if (data instanceof Blob) {
          try {
            msg = (JSON.parse(await data.text()) as { error?: { message?: string } })?.error?.message;
          } catch {
            msg = undefined; // not JSON — fall back to the generic message below
          }
        }
        notify.error(msg ?? `${action.label} failed.`);
      }
      return;
    }
    const ok = await confirmDlg({ title: action.confirmTitle, confirmLabel: action.label, message: action.confirmMessage ?? `${action.label} the selected rows?` });
    if (!ok) return;
    try {
      await api.post(url, { ids });
      invalidatePreviews();
      notify.success(action.successMessage);
      setSelected(new Set());
      await refreshList();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      notify.error(msg ?? `${action.label} failed.`);
    }
  };

  // Commit a choice action once the user picks a value in ListActionChoiceDialog: one POST of the
  // ticked ids + the chosen value (under action.choiceKey, default "status") + any extraFields (e.g.
  // Close Prospects → actualRevenue + closeDate), then success + refresh.
  const runChoiceAction = async (action: CrudListAction, value: string, extras: Record<string, string> = {}) => {
    if (!crud?.basePath) return;
    const ids = Array.from(selected);
    if (ids.length === 0) { setChoiceAction(null); return; }
    const url = `${crud.basePath}/${action.path}${action.query ? `?${action.query}` : ""}`;
    try {
      await api.post(url, { ids, [action.choiceKey ?? "status"]: value, ...extras });
      invalidatePreviews();
      notify.success(action.successMessage);
      setSelected(new Set());
      setChoiceAction(null);
      await refreshList();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      notify.error(msg ?? `${action.label} failed.`);
    }
  };

  const doReactivateCharges = async (rowId?: string) => {
    const ids = rowId ? [rowId] : Array.from(selected);
    if (ids.length === 0) return;
    const ok = await confirmDlg({
      title: "Reactivate",
      confirmLabel: "Reactivate",
      message: `Reactivate ${ids.length} item charge${ids.length === 1 ? "" : "s"}?`,
    });
    if (!ok) return;
    try {
      for (const id of ids) await api.post(`/api/v1/item-charges/${id}/reactivate`);
      notify.success("Reactivated successfully.");
      setSelected(new Set());
      await refreshList();
    } catch {
      notify.error("Could not reactivate the item charge.");
    }
  };

  // Retired Document ▸ Reactivate (un-retire) — legacy XAF "Reactivate" action.
  // Confirm ("Do you want reactivate the document?", legacy wording) → POST → the row,
  // now IsRetire=0, drops out of this IsRetire=1 list on refresh. Callable two ways:
  // the toolbar button (uses the single checked row) OR the per-row ↻ button in the
  // leading column (passes that row's id directly — legacy green-arrow cell, image 212).
  const doReactivate = async (rowId?: string) => {
    const id = rowId ?? (selected.size === 1 ? Array.from(selected)[0]! : undefined);
    if (!id) return;
    const ok = await confirmDlg({
      title: "Reactivate",
      confirmLabel: "Reactivate",
      message: "Do you want reactivate the document?",
    });
    if (!ok) return;
    try {
      await api.post(`/api/v1/documents/${id}/reactivate`);
      notify.success("Reactivated successfully.");
      setSelected(new Set());
      await refreshList();
    } catch {
      notify.error("Could not reactivate the document.");
    }
  };

  // ── Bulk load (Import + Template) — only for crud.bulkLoad lookups (Method) ──
  const importRef = useRef<HTMLInputElement>(null);
  // Template: a blank CSV of the non-readonly field labels (the same headers the
  // import endpoint binds).  Client-side — no backend needed.
  // NOTE: the Import/Template toolbar buttons are hidden for now
  // (user 2026-07-02) — the handlers stay wired for their return.
  const downloadTemplate = () => {
    if (!crud) return;
    const headers = crud.fields.filter((f) => f.type !== "readonly").map((f) => f.label);
    const url = URL.createObjectURL(new Blob(["﻿" + headers.join(",") + "\r\n"], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${crud.entityLabel}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const pickImportFile = () => importRef.current?.click();
  // Keep the two handlers referenced while their toolbar buttons are hidden
  // (tsc noUnusedLocals) — remove these voids when the buttons return.
  void downloadTemplate;
  void pickImportFile;
  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file || !crud) return;
    const parsed = parseCsv(await file.text());
    if (parsed.length === 0) {
      notify.warning("No rows found in the file.");
      return;
    }
    try {
      const res = await runLoading(
        api.post<{ data?: { created?: number; failed?: number } }>(`${crud.basePath}/import`, parsed),
        { blocking: true, label: `Importing ${parsed.length} row${parsed.length > 1 ? "s" : ""}…` },
      );
      const created = res.data?.data?.created ?? 0;
      const failed = res.data?.data?.failed ?? 0;
      await refreshList();
      const noun = crud.entityLabel.toLowerCase();
      if (failed > 0)
        notify.warning(
          `Imported ${created} ${noun}${created === 1 ? "" : "s"}; ${failed} row${failed > 1 ? "s" : ""} skipped.`,
        );
      else notify.success(`Imported ${created} ${noun}${created === 1 ? "" : "s"}.`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ??
        "Import failed.";
      notify.error(msg);
    }
  };

  // Items "Import File" / "Export items stock" (ALPACA parity, user request 2026-09-23,
  // Settings ▸ Utility ▸ Inventory Management ▸ Items toolbar). Legacy's real bulk
  // find-or-create/accumulate Excel import — materially different from the generic CSV
  // bulk-load above — so it gets its own ref + handlers, scoped to this one form.
  const itemsImportRef = useRef<HTMLInputElement>(null);
  const pickItemsImportFile = () => itemsImportRef.current?.click();
  const onItemsImportFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await runLoading(
        api.post<{ data?: { created: number; updated: number } }>("/api/v1/inventory-items/import", form),
        { blocking: true, label: `Importing ${file.name}…` },
      );
      const d = res.data?.data;
      await refreshList();
      notify.success(`Imported ${d?.created ?? 0} new item(s); updated ${d?.updated ?? 0}.`);
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { error?: { message?: string; details?: string[] } } } }).response
        ?.data?.error;
      const details = resp?.details ?? [];
      const shown = details.slice(0, 5).join(" · ");
      const msg = resp?.message ?? "Import failed.";
      notify.error(
        details.length > 0 ? `${msg} ${shown}${details.length > 5 ? ` (+${details.length - 5} more)` : ""}` : msg,
      );
    }
  };
  // crud.listImport — the legacy Import File / Template Download pair (Parameter Library).
  const [listImportOpen, setListImportOpen] = useState(false);
  const downloadListImportTemplate = async () => {
    const cfg = crud?.listImport;
    if (!cfg?.templateUrl) return;
    try {
      const res = await runLoading(api.get<Blob>(cfg.templateUrl, { responseType: "blob" }), {
        blocking: true,
        label: "Preparing template…",
      });
      await saveBlobAs(res.data, cfg.templateName ?? "template.csv");
    } catch {
      notify.error("Could not download the template.");
    }
  };
  const exportItemsStockTemplate = async () => {
    try {
      const res = await runLoading(api.get<Blob>("/api/v1/inventory-items/export-template", { responseType: "blob" }), {
        blocking: true,
        label: "Preparing template…",
      });
      await saveBlobAs(res.data, "itemtemplate.xlsx");
    } catch {
      notify.error("Could not download the template.");
    }
  };
  // Mirrors AppToolbar's own private `tip()` — shows the EFFECTIVE, per-tenant-
  // remapped combo (standing rule 14) on the inline buttons above.
  const hotkeyTip = (label: string, combo: string) => `${label} (${formatCombo(effectiveCombo(combo))})`;
  // "a bespoke feature form with a hand-rolled action row registers its buttons
  // with a SINGLE useHotkeys([...]) call" (standing rule 14) — AppToolbar's own
  // New/Edit/Delete hotkeys are suppressed for this module (its onNew/onEdit/
  // onDelete props are undefined above), so they're re-registered here alongside
  // Import File. "Export items stock" gets no global key — Alt+X is already the
  // real "Export to" dropdown's (exportData, unchanged, still AppToolbar's own).
  useHotkeys(
    moduleKey === "set-inventory-items" && crud
      ? [
          { combo: "alt+n", label: "New", group: "Toolbar", order: 1, run: () => openNew() },
          {
            combo: "alt+e",
            label: "Edit",
            group: "Toolbar",
            order: 2,
            disabled: selected.size !== 1,
            run: () => openEditSelected(),
          },
          {
            combo: "alt+d",
            label: "Delete",
            group: "Toolbar",
            order: 3,
            disabled: selected.size === 0 || bulkDeleting,
            run: () => deleteSelected(),
          },
          { combo: "alt+i", label: "Import File", group: "Toolbar", order: 6, run: () => pickItemsImportFile() },
        ]
      : [],
  );

  const toggleRow = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else if (id) n.add(id);
      return n;
    });
  // Bulk delete the checked rows (ALPACA "Delete" toolbar action).
  const deleteSelected = async () => {
    if (!crud || selected.size === 0) return;
    const ids = Array.from(selected);
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native window.confirm).
    const okDel = await confirmDlg({
      title: "Delete",
      tone: "danger",
      confirmLabel: "Delete",
      message: `Delete ${ids.length} ${crud.entityLabel.toLowerCase()}${ids.length > 1 ? "s" : ""}? This cannot be undone.`,
    });
    if (!okDel) return;
    // Reason-for-change gate (standing rule 11) — prompts only when this module's
    // form has ReasonRequired ON; null = cancelled → abort.  runWithAuditReason
    // arms X-Audit-Reason so the generic delete's audit row carries the reason.
    const reason = await gateReason(crud.formName, "delete");
    if (reason === null) return;
    setBulkDeleting(true);
    let failed = 0;
    // First reason the API gave for refusing (e.g. 409 "Deletion restricted. …").
    let refusal: string | undefined;
    await runLoading(
      runWithAuditReason(reason, async () => {
        for (const rid of ids) {
          try {
            await api.delete(`${crud.basePath}/${rid}`);
          } catch (e) {
            failed++;
            refusal ??=
              (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
              undefined;
          }
        }
      }),
      {
        blocking: true,
        label: `Deleting ${ids.length} ${crud.entityLabel.toLowerCase()}${ids.length > 1 ? "s" : ""}…`,
      },
    );
    setBulkDeleting(false);
    setSelected(new Set());
    await refreshList();
    if (failed > 0) notify.error(`${failed} of ${ids.length} could not be deleted.${refusal ? ` ${refusal}` : ""}`);
    else notify.success(ids.length === 1 ? "Deleted successfully." : `Deleted ${ids.length} ${crud.entityLabel.toLowerCase()}s successfully.`);
  };

  // ── UI-8: inline (in-cell) editing ───────────────────────────────────────
  // Editable text/textarea/date cells commit through the SAME update endpoint
  // the Edit dialog uses (GET the full record → merge the one changed field →
  // PUT), so the existing MediatR/EF update handler runs — audit
  // (IAuditTrailService) and UTC persistence happen server-side, never bypassed.
  // IDs, readonly, select and checkbox columns stay read-only here (edit them in
  // the dialog).
  const crudFieldByKey = useMemo(() => {
    const m: Record<string, CrudField> = {};
    if (crud) for (const f of crud.fields) m[f.key] = f;
    return m;
  }, [crud]);
  const inlineType = (key: string): "text" | "textarea" | "date" | "select" | "multiselect" | null => {
    // inlineRowEdit === false ⇒ no in-cell double-click editing (these simple
    // lists open the full detail on row-click / Edit instead). BO UI Standard.
    if (!crud || crud.inlineRowEdit === false || key === crud.idField) return null;
    const f = crudFieldByKey[key];
    if (!f) return null;
    // Inline DROPDOWN cell — OPT-IN per field via inlineType:'select' (only fields
    // that declare it, e.g. eNotification's FK/enum columns, become inline selects;
    // every other select stays edit-in-detail, so no other form changes).
    if (f.inlineType === "select" && (f.options || f.optionsUrl)) return "select";
    // Checkbox-popup cell — OPT-IN per field via inlineType:'multiselect' + optionsUrl (G5 Parameter popup).
    if (f.inlineType === "multiselect" && f.optionsUrl) return "multiselect";
    return f.type === "text" || f.type === "textarea" || f.type === "date" ? f.type : null;
  };
  // Options for inline-select cells: static `options` are read directly in render;
  // `optionsUrl` lists are fetched once here (legacy scrollable FK combos).
  const [inlineSelOpts, setInlineSelOpts] = useState<Record<string, { id: string; name: string }[]>>({});
  useEffect(() => {
    if (!crud) return;
    const urlFields = crud.fields.filter((f) => f.inlineType === "select" && f.optionsUrl);
    if (urlFields.length === 0) return;
    let alive = true;
    void (async () => {
      const entries = await Promise.all(
        urlFields.map(async (f) => {
          try {
            const r = await api.get<{ data?: { id: unknown; name: unknown }[] }>(f.optionsUrl!, {
              silentLoading: true,
            } as never);
            const opts = (r.data?.data ?? []).map((o) => ({ id: String(o.id ?? ""), name: String(o.name ?? "") }));
            return [f.key, opts] as const;
          } catch {
            return [f.key, [] as { id: string; name: string }[]] as const;
          }
        }),
      );
      if (alive) setInlineSelOpts(Object.fromEntries(entries));
    })();
    return () => {
      alive = false;
    };
  }, [crud, moduleKey]);
  const [editCell, setEditCell] = useState<{ id: string; key: string } | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingCell, setSavingCell] = useState(false);
  const startEdit = (rowId: string, key: string, raw: unknown) => {
    if (!rowId) return;
    setEditDraft(inlineType(key) === "date" ? toDateInputLocal(raw) : raw == null ? "" : String(raw));
    setEditCell({ id: rowId, key });
  };
  const cancelEdit = () => setEditCell(null);
  // `override` lets a <select> commit with its fresh value in the same event, so
  // the async setEditDraft doesn't race the save (text/date still pass none).
  const commitEdit = async (override?: string) => {
    if (!crud || !editCell) return;
    const f = crudFieldByKey[editCell.key];
    const draftVal = override ?? editDraft;
    const draft = draftVal.trim();
    // Client-side validation (server FluentValidation stays authoritative).
    if (f?.required && draft === "") {
      notify.warning(`${f.label} is required.`);
      return;
    }
    if (f?.maxLength && draft.length > f.maxLength) {
      notify.warning(`${f.label} cannot exceed ${f.maxLength} characters.`);
      return;
    }
    // Reason-for-change gate (standing rule 11) — inline edit is an Update; prompt
    // only when this module's form has ReasonRequired ON. null = cancelled → abort.
    const reason = await gateReason(crud.formName, "edit");
    if (reason === null) return;
    setSavingCell(true);
    try {
      await runWithAuditReason(reason, async () => {
        // Reuse the existing update path: pull the full current record, override
        // the one edited field, PUT it back through the audited handler.
        const res = await api.get<{ data?: Record<string, unknown> }>(`${crud.basePath}/${editCell.id}`);
        const rec = res.data?.data ?? {};
        const payload: Record<string, unknown> = {};
        for (const fld of crud.fields) {
          if (fld.type === "readonly") continue;
          const source = fld.key === editCell.key ? draftVal : rec[fld.key];
          if (fld.type === "checkbox") payload[fld.key] = Boolean(source);
          else if (fld.type === "multiselect") {
            // Preserve the array as-is — NEVER String()-ify it, or a multiselect
            // (e.g. Department) is corrupted when an unrelated cell is edited inline.
            payload[fld.key] = Array.isArray(source) ? source : source == null || source === "" ? [] : [String(source)];
          } else if (fld.type === "date") {
            const s = toDateInputLocal(source);
            payload[fld.key] = s === "" ? null : s;
          } else {
            const s = source == null ? "" : String(source).trim();
            payload[fld.key] = s === "" ? null : s;
          }
        }
        await api.put(`${crud.basePath}/${editCell.id}`, payload);
      });
      setEditCell(null);
      await refreshList();
      notify.success("Change saved.");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ??
        "Save failed.";
      notify.error(msg);
    } finally {
      setSavingCell(false);
    }
  };

  // ── Legacy XAF ROW inline editor (crud.pencilRowEdit) ──────────────────────
  // The Edit pencil puts the WHOLE row into editors at once; Save writes every
  // inline-editable field in one audited PUT (non-inline fields — a multiselect,
  // the computed Active column — are preserved from the fetched record). Opt-in
  // per module, so per-cell double-click grids are unaffected.
  const rowEditable = crud?.pencilRowEdit === true;
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [rowDraft, setRowDraft] = useState<Record<string, string>>({});
  // Checkbox popup for a multi-select cell (ParameterPickerDialog). `multiNames` keeps the names of the
  // ids picked in this session so the cell can show them without another fetch.
  const [multiPopup, setMultiPopup] = useState<
    { rid: string; key: string; title: string; url: string; initial?: string[] } | null
  >(null);
  const [multiNames, setMultiNames] = useState<Record<string, Record<string, string>>>({});
  const openMultiPopup = (rid: string, key: string, fld?: CrudField) => {
    if (!fld?.optionsUrl) return;
    const draft = rowDraft[key];
    setMultiPopup({
      rid,
      key,
      title: fld.label,
      url: fld.optionsUrl.replace("{id}", rid),
      initial: draft === undefined ? undefined : String(draft).split(";").filter(Boolean),
    });
  };
  const multiCellLabel = (key: string, draft: string | undefined, fallback: unknown): string => {
    if (draft === undefined) return fallback == null || fallback === "" ? "— None —" : String(fallback);
    const ids = draft.split(";").filter(Boolean);
    if (ids.length === 0) return "— None —";
    const names = multiNames[key] ?? {};
    return ids.map((id) => names[id] ?? id).join(", ");
  };
  // "+" beside a row-edit dropdown with quickAdd (G5 Method row): the same popup the detail form opens.
  const [quickAddKey, setQuickAddKey] = useState<string | null>(null);
  const startRowEdit = (row: Record<string, unknown>) => {
    if (!crud) return;
    const rid = String(row[crud.idField] ?? "");
    if (!rid) return;
    const draft: Record<string, string> = {};
    for (const f of crud.fields) {
      const it = inlineType(f.key);
      // A popup field is not seeded: it is only sent when the user picks in the popup (so the row
      // save never clears links the user did not touch).
      if (!it || it === "multiselect") continue;
      // Only a field the row actually carries (a list column) is edited on the row. A detail-only
      // field (e.g. Parameter Library's Analyte Code) is absent from the row, so seeding it would
      // put "" in the draft and the save would blank the stored value; left out, saveRow keeps it
      // from the fetched record.
      if (!(f.key in row) && !(`${f.key}Id` in row)) continue;
      const seed = it === "select" ? (row[`${f.key}Id`] ?? row[f.key]) : row[f.key];
      draft[f.key] = it === "date" ? toDateInputLocal(seed) : seed == null ? "" : String(seed);
    }
    setRowDraft(draft);
    setEditRowId(rid);
  };
  const cancelRowEdit = () => {
    setEditRowId(null);
    setRowDraft({});
  };
  const saveRow = async () => {
    if (!crud || !editRowId) return;
    // Client-side validation across the editable fields (server stays authoritative).
    for (const f of crud.fields) {
      if (!inlineType(f.key) || !Object.prototype.hasOwnProperty.call(rowDraft, f.key)) continue;
      const v = (rowDraft[f.key] ?? "").trim();
      if (f.required && v === "") {
        notify.warning(`${f.label} is required.`);
        return;
      }
      if (f.maxLength && v.length > f.maxLength) {
        notify.warning(`${f.label} cannot exceed ${f.maxLength} characters.`);
        return;
      }
    }
    const reason = await gateReason(crud.formName, "edit");
    if (reason === null) return;
    setSavingCell(true);
    try {
      await runWithAuditReason(reason, async () => {
        // Pull the full current record, override the edited fields, PUT it back
        // through the audited handler — non-inline fields survive untouched.
        const res = await api.get<{ data?: Record<string, unknown> }>(`${crud.basePath}/${editRowId}`);
        const rec = res.data?.data ?? {};
        const payload: Record<string, unknown> = {};
        for (const fld of crud.fields) {
          const edited = Object.prototype.hasOwnProperty.call(rowDraft, fld.key) && !!inlineType(fld.key);
          // A read-only field is sent only when a popup picked a new value for it.
          if (fld.type === "readonly" && !(edited && inlineType(fld.key) === "multiselect")) continue;
          const source = edited ? rowDraft[fld.key] : rec[fld.key];
          if (edited && inlineType(fld.key) === "multiselect") {
            // Draft holds the picked ids joined by ";" — the server takes them as a list.
            payload[fld.key] = String(source ?? "").split(";").filter(Boolean);
          } else if (fld.type === "checkbox") payload[fld.key] = Boolean(source);
          else if (fld.type === "multiselect")
            payload[fld.key] = Array.isArray(source) ? source : source == null || source === "" ? [] : [String(source)];
          else if (fld.type === "date") {
            const s = toDateInputLocal(source);
            payload[fld.key] = s === "" ? null : s;
          } else {
            const s = source == null ? "" : String(source).trim();
            payload[fld.key] = s === "" ? null : s;
          }
        }
        // A select with idKey posts its id under that key (as the detail form does).
        for (const fld of crud.fields) {
          if (fld.type === "select" && fld.idKey && fld.key in payload) {
            payload[fld.idKey] = payload[fld.key];
            delete payload[fld.key];
          }
        }
        await api.put(`${crud.basePath}/${editRowId}`, payload);
      });
      setEditRowId(null);
      setRowDraft({});
      await refreshList();
      notify.success("Change saved.");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ??
        "Save failed.";
      notify.error(msg);
    } finally {
      setSavingCell(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const setColFilter = (key: string, values: string[]) => {
    setFilters((f) => {
      const n = { ...f };
      if (values.length) n[key] = values;
      else delete n[key];
      return n;
    });
    setPage(1);
  };

  // Grouped forms load every row (no server paging) so the client can build
  // complete role groups + counts; the cap is the SP's 10k clamp.
  const groupBy = GROUPED_BY[moduleKey];
  // §9 fuzzy search for small Class A/B lookups (Method, Unit, QC Type, Holidays,
  // …): CRUD lookups that aren't enhanced / advanced / grouped. While the user is
  // typing, load the full set ONCE and fuzzy-filter + paginate in the browser so a
  // subsequence like "mbwkly" finds "Main Biweekly". Idle (empty box) keeps normal
  // server pagination, so a large lookup isn't fully loaded until it's searched.
  // Big server-paginated lists keep substring server search (a backend subsequence
  // param is a separate task).
  // §2/§9 — the class drives fuzzy eligibility: small Class A (lookup) / B (master
  // data) lists get client-side fuzzy; Class C/D/E/F keep server search.
  const clientFuzzy = (boClass === "A" || boClass === "B") && !enhanced && !advanced && !groupBy;
  // The global Search box (EXACT prototype .gsearch glyph). Rendered on its own row under the
  // action bar, or ON the bar after Export to when the module sets `searchInToolbar`.
  const searchBox = (
    <div className="list-search">
      <span className="tb-i">🔍</span>
      <input
        type="text"
        placeholder="Search…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />
      {searchInput && (
        <span className="list-search-clear" onClick={() => setSearchInput("")}>
          ×
        </span>
      )}
    </div>
  );
  const fuzzySearching = clientFuzzy && search.trim() !== "";
  const listParams = {
    page: fuzzySearching ? 1 : page,
    pageSize: fuzzySearching || groupBy ? 10000 : wirePageSize(pageSize),
    search: fuzzySearching ? "" : search,
    // Committed Date Range; a custom From/To (Date Received in the panel) overrides it.
    rangeDays: effectiveRange,
    // A scope tab pins its column on top of whatever the user filtered by hand.
    filters: scopeTabs
      ? { ...filters, [scopeTabs.column]: [scopeTabs.tabs[scopeTab]?.value ?? ""] }
      : filters,
    sort: sortCol,
    sortDesc,
    fromDate: advanced ? fromDate || null : null,
    toDate: advanced ? toDate || null : null,
    analysisFrom: advanced ? analysisFrom || null : null,
    analysisTo: advanced ? analysisTo || null : null,
    collectedFrom: advanced ? collectedFrom || null : null,
    collectedTo: advanced ? collectedTo || null : null,
    // Server-side so a chip matches across EVERY page, and so it can match one
    // item of the "; "-joined category list rather than the whole string.
    category: isTestCode ? category : null,
  };
  const { data, isFetching, isError, error } = useModuleList(moduleKey, listParams);
  const mod = data?.module;
  // Preserve the column order exactly as the backend registry declares it.
  // ModuleRegistry hand-orders every module's columns to match the ALPACA
  // Go-Live layout, so we must NOT re-sort here (alphabetical sorting would
  // destroy that deliberate order).
  // Module-wide column width from the shell (Data Center = G5 ListView 140px,
  // 2026-10-13) — the SAME default <AppDataGrid> reads, so every grid in the
  // module matches.
  const gridLayout = useGridLayoutDefaults();
  const { fixedColumnWidth } = gridLayout;
  const cols = useMemo(() => {
    const base = mod?.columns ?? [];
    // MSR: un-pin the MSRID (first) column. A ~9-column grid needs no frozen
    // column, and the sticky-left offset (which ignores the leading checkbox)
    // misaligns the pinned cell — showing wrong once the Edit column was removed
    // (user request 2026-07-15). Other modules keep their frozen columns.
    // A scope-tabbed list hides the column its tabs pin: the active tab already says
    // which value every row has, so the column would repeat it on all of them.
    const scopeColumn = LIST_SCOPE_TABS[moduleKey]?.column;
    const scoped = scopeColumn ? base.filter((c) => c.key !== scopeColumn) : base;
    // Module column width: every column starts at it (frozen offsets, export and
    // the header all read c.width, so they stay consistent). A user's
    // drag-resize still wins — the column manager stores it per column.
    if (fixedColumnWidth !== undefined && !G5_WIDTH_MODULES.has(moduleKey)) return scoped.map((c) => ({ ...c, width: moduleColumnWidth(gridLayout, c.key) ?? fixedColumnWidth }));
    return moduleKey === "msr" ? scoped.map((c) => (c.frozen ? { ...c, frozen: false } : c)) : scoped;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod?.columns, moduleKey, gridLayout]);
  // The column the grid SHOWS as sorted: the user's choice, else — in a module
  // that defaults to its first column (Data Center) — the first column, which
  // the server orders by when no sort is sent (ModuleListReader). Result
  // Samples is excluded: its fast UNION path keeps its own indexed order.
  const shownSortCol =
    sortCol ?? (defaultSortFirstColumn && !NO_FIRST_COL_DEFAULT.has(moduleKey) ? cols[0]?.key ?? null : null);
  // Direction of that shown sort: the user's, else the module's default.
  const shownSortDesc = sortCol ? sortDesc : FIRST_COL_DESC.has(moduleKey);
  // Shared column model — Hide / Show-all / Column Chooser / drag-reorder /
  // resize — so this hand-rolled list behaves exactly like <AppDataGrid>. The
  // grid renders from cm.visibleColumns; the header menu gets the column items
  // via cm.columnMenu. (Created here, after its source `cols`.)
  // persistKey = moduleKey → each Data Center form restores its own column
  // visibility / order / widths on return (the component remounts per module).
  const cm = useColumnManager(
    cols,
    (c) => c.key,
    (c) => c.label,
    moduleKey,
  );
  const { openHeaderMenu, headerMenu } = useHeaderMenu(headerSort, cm.columnMenu);
  // Default filter-list direction (CLAUDE.md §18.6, 2026-06-18): a column that
  // is NOT the active sort column still lists its filter values in the grid's
  // natural order — DESC when the first column is an ID (records newest-first),
  // else ASC.  The active sort column overrides this with its own direction.
  const filterDefaultDir: "asc" | "desc" = useMemo(() => {
    const first = cols[0];
    const isId = !!first && (/ID$/.test(first.label ?? "") || /Id$/.test(first.key ?? ""));
    return isId ? "desc" : "asc";
  }, [cols]);
  const rawRows = data?.data ?? [];
  const meta = data?.meta;
  // Client-side fuzzy (§9): filter + rank the full loaded set, then paginate in the
  // browser. No-op (rawRows unchanged) unless the user is actively fuzzy-searching a
  // client-fuzzy lookup. `search` ranks best-match first; empty query keeps DB order.
  const rowSearchText = (r: Record<string, unknown>) => cols.map((c) => fmt(r[c.key], c.dateOnly, c.currency)).join(" ");
  const fuzzyFiltered = fuzzySearching ? fuzzyFilter(rawRows, search, rowSearchText) : rawRows;
  const rows = fuzzySearching ? fuzzyFiltered.slice((page - 1) * pageSize, page * pageSize) : rawRows;
  const totalPages = fuzzySearching
    ? Math.max(1, Math.ceil(fuzzyFiltered.length / pageSize))
    : Math.max(1, meta?.totalPages ?? 1);
  // One source for the header + footer count / page label across both modes.
  const displayCount = fuzzySearching ? fuzzyFiltered.length : (meta?.totalCount ?? 0);
  const displayPage = fuzzySearching ? page : (meta?.page ?? 1);
  const mapped = mod?.mapped ?? false;
  // CRUD selection helpers (depend on the current page's rows).  No-op when the
  // module isn't CRUD-enabled (crud === undefined).
  const pageIds = useMemo(
    () => (crud || selectableList ? rows.map((r) => String((r as Record<string, unknown>)[selIdField] ?? "")).filter(Boolean) : []),
    [crud, selectableList, selIdField, rows],
  );
  const pageAllSelected = (!!crud || selectableList) && pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = (checked: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (checked) pageIds.forEach((id) => n.add(id));
      else pageIds.forEach((id) => n.delete(id));
      return n;
    });
  // First load of THIS module: the resolved data still belongs to the previous
  // module (keepPreviousData), so show only a loading spinner — never the
  // "data source mapping in progress" note — until this module's data arrives.
  const loadingThisModule = isFetching && mod?.key !== moduleKey;

  // ── Keyboard row navigation for the CRUD list (mouse-free, standing rule 14) ──
  // This hand-rolled table mirrors <AppDataGrid>: on OPEN (and after a data reload)
  // focus lands on the FIRST row so a List/Detail form (Collectors, every Settings
  // master, …) is keyboard-drivable without hunting for focus with Tab (user request
  // 2026-09-21). ↑/↓ move the active row (crossing pages at the edges), Home/End jump,
  // PageUp/PageDown page, Enter opens the row (its view), Space toggles its selection.
  // Only for CRUD lists — a read-only Data Center grid has no row action and stays a
  // plain scroll list. The active-row RING is driven by :focus-within (CSS), so it
  // never lingers after a mouse click elsewhere (same as AppDataGrid's .adg-nav).
  // plainList (legacy XAF chrome) turns OFF keyboard grid-nav, which also drops the
  // orange active-row focus ring and the auto-focus of the first row (user 2026-09-22).
  const gridNav = !!crud && !crud.plainList;
  // Enter opens the same thing a row-cell click does (the read-only view); mirrors
  // the per-cell `rowClickView` computed in the row render below.
  const rowOpensView = !crud?.readOnlyList && (crud?.inlineRowEdit === false || crud?.pencilRowEdit === true);
  const [activeRow, setActiveRow] = useState(-1);
  // Cell-by-cell Tab navigation (ARIA grid model, user request 2026-09-28): Tab
  // walks the DATA columns of the focused row and wraps to the next row's first
  // cell; ↑/↓ still move rows but KEEP the active column. Cells are focused via the
  // row's live DOM `cells` collection (this list renders many bespoke <td> variants,
  // so a per-cell ref/tabindex prop would touch every one) — the cell's tabIndex is
  // set to −1 the moment before focusing, and the active column is read back from
  // the focused cell's position at keydown time (no separate state needed).
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const pendingRowFocus = useRef<null | "first" | "last">(null);
  const pendingColRef = useRef<number>(-1);
  // Whether the user is actively driving this list with the KEYBOARD. Starts false,
  // flips true on a real navigation keydown inside the grid, and a mouse press
  // clears it. The orange active-row/-cell ring is gated on this (class `.mlv-kbd`),
  // so it NEVER shows on the programmatic auto-focus that fires when the list loads
  // its first row (Chrome marks that focus :focus-visible when the last interaction
  // was keyboard, so :focus-visible alone wasn't enough — user report "orange first
  // row on page open", 2026-09-28), and never on a mouse click. Keeps keyboard-nav
  // feedback (rule 14).
  const [kbdActive, setKbdActive] = useState(false);

  const focusGridRow = (idx: number) => {
    const el = rowRefs.current[idx];
    if (el) {
      el.focus();
      el.scrollIntoView?.({ block: "nearest" });
    }
  };

  // G5 Instrument: the open-detail icon drawn in its own column after the pencil.
  const detailCol =
    !!crud && !!crud.detailIcon && !!crud.detailIconColumn && !crud.hideEditPencil && !crud.pureListView && perm.write;

  // Number of leading NON-DATA cells (selection checkbox + edit-pencil) before the
  // first data column — so a DOM cell index maps to a data-column index.
  const leadCols = crud && !crud.pureListView ? 1 + (crud.hideEditPencil ? 0 : 1) + (detailCol ? 1 : 0) : selectableList ? 1 + (prospectsEditable ? 1 : 0) : 0;

  // The data-column index of the currently-focused cell within its row (−1 = the
  // row itself or a leading action cell holds focus).
  const currentDataCol = (t: HTMLElement, tr: HTMLTableRowElement): number => {
    const td = t.closest("td");
    if (!td || td.parentElement !== tr) return -1;
    const idx = Array.prototype.indexOf.call(tr.cells, td);
    const dc = idx - leadCols;
    return dc >= 0 ? dc : -1;
  };

  // Focus one data cell of a row, remembering its column so ↑/↓ can preserve it.
  const focusDataCell = (rowIdx: number, dataCol: number) => {
    const tr = rowRefs.current[rowIdx];
    if (!tr) return;
    const target = tr.cells[leadCols + dataCol] as HTMLTableCellElement | undefined;
    if (target) {
      target.tabIndex = -1;
      setActiveRow(rowIdx);
      target.focus();
      target.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    } else {
      focusGridRow(rowIdx);
    }
  };

  const dataColCount = (rowIdx: number): number => {
    const tr = rowRefs.current[rowIdx];
    return tr ? tr.cells.length - leadCols : 0;
  };

  const onRowKeyDown = (e: ReactKeyboardEvent<HTMLTableRowElement>, i: number, row: Record<string, unknown>) => {
    // A keydown on a focused row means the user is driving the list by keyboard —
    // reveal the active-row/-cell ring (gated by `.mlv-kbd`) from here on. Cleared
    // again by a mouse press on the table (see the scroll wrapper's onMouseDown).
    if (!kbdActive) setKbdActive(true);
    // Keydown bubbles from whatever is focused inside the row: an inline TEXT editor
    // owns EVERY key; a focused button / link / checkbox keeps Enter + Space (native
    // activate / toggle) but arrows still navigate rows.
    const t = e.target as HTMLElement;
    if (t !== e.currentTarget) {
      const tag = t.tagName;
      const isTextEntry =
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable ||
        (tag === "INPUT" && !/^(checkbox|radio|button|submit|reset)$/i.test((t as HTMLInputElement).type || ""));
      if (isTextEntry) return;
      if (
        (e.key === "Enter" || e.key === " " || e.key === "Spacebar") &&
        (tag === "BUTTON" || tag === "A" || tag === "INPUT")
      )
        return;
    }
    // Preserve the active data-column across vertical moves once the user has
    // Tabbed into a cell (−1 = still row-level).
    const col = currentDataCol(t, e.currentTarget);
    const toRowOrCell = (idx: number) => { if (col >= 0) focusDataCell(idx, col); else focusGridRow(idx); };
    switch (e.key) {
      case "Tab": {
        // Cell-by-cell across the DATA columns (ARIA grid model, user request
        // 2026-09-28). A focused text / inline editor owns Tab (returned above via
        // the isTextEntry guard). Not preventing default at the true grid edge lets
        // Tab / Shift+Tab escape onto the next / previous form control.
        const ncols = dataColCount(i);
        if (ncols <= 0) break;
        if (!e.shiftKey) {
          if (col < 0) { e.preventDefault(); focusDataCell(i, 0); }
          else if (col < ncols - 1) { e.preventDefault(); focusDataCell(i, col + 1); }
          else if (i < rows.length - 1) { e.preventDefault(); focusDataCell(i + 1, 0); }
          else if (page < totalPages) { e.preventDefault(); pendingRowFocus.current = "first"; pendingColRef.current = 0; setPage(page + 1); }
          // else: last cell of last row → let Tab exit the grid naturally
        } else {
          if (col > 0) { e.preventDefault(); focusDataCell(i, col - 1); }
          else if (col === 0 && i > 0) { e.preventDefault(); focusDataCell(i - 1, dataColCount(i - 1) - 1); }
          else if (col === 0 && page > 1) { e.preventDefault(); pendingRowFocus.current = "last"; pendingColRef.current = Number.MAX_SAFE_INTEGER; setPage(page - 1); }
          // else (row-level, or first cell of first row): let Shift+Tab exit naturally
        }
        break;
      }
      case "ArrowDown":
        e.preventDefault();
        if (i < rows.length - 1) {
          setActiveRow(i + 1);
          toRowOrCell(i + 1);
        } else if (page < totalPages) {
          pendingRowFocus.current = "first";
          pendingColRef.current = col;
          setPage(page + 1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (i > 0) {
          setActiveRow(i - 1);
          toRowOrCell(i - 1);
        } else if (page > 1) {
          pendingRowFocus.current = "last";
          pendingColRef.current = col;
          setPage(page - 1);
        }
        break;
      case "Home":
        e.preventDefault();
        setActiveRow(0);
        toRowOrCell(0);
        break;
      case "End": {
        e.preventDefault();
        const last = rows.length - 1;
        setActiveRow(last);
        toRowOrCell(last);
        break;
      }
      case "PageDown":
        if (page < totalPages) {
          e.preventDefault();
          pendingRowFocus.current = "first";
          setPage(page + 1);
        }
        break;
      case "PageUp":
        if (page > 1) {
          e.preventDefault();
          pendingRowFocus.current = "first";
          setPage(page - 1);
        }
        break;
      case "Enter":
        e.preventDefault();
        // Enter opens the detail when a row click does (rowOpensView) OR when the
        // form opts into double-click-to-open (rowDoubleClickView) — keyboard parity.
        if (rowOpensView || crud?.rowDoubleClickView) openView(row);
        break;
      case " ":
      case "Spacebar":
        if (crud) {
          e.preventDefault();
          toggleRow(String(row[crud.idField] ?? ""));
        }
        break;
      default:
        break;
    }
  };

  // Roving tabindex: the active row is the single Tab stop; before the user has moved,
  // the first row carries it so a Tab lands on the grid.
  const rowTabIndex = (i: number): number | undefined =>
    !gridNav ? undefined : activeRow < 0 ? (i === 0 ? 0 : -1) : i === activeRow ? 0 : -1;

  // Clamp the active row to the current page, and apply a pending cross-page focus
  // (arrow at an edge → setPage → focus first/last of the new page). Runs every render
  // (rows is a fresh slice) but the else-branch is an idempotent clamp, so React bails.
  useEffect(() => {
    if (!gridNav) return;
    if (pendingRowFocus.current) {
      const idx = pendingRowFocus.current === "last" ? rows.length - 1 : 0;
      const wantCol = pendingColRef.current;
      pendingRowFocus.current = null;
      pendingColRef.current = -1;
      if (idx >= 0) {
        setActiveRow(idx);
        if (wantCol >= 0) {
          // MAX_SAFE_INTEGER is the "last column" sentinel (Shift+Tab into the
          // previous page); otherwise land on the same column the move came from.
          const cols = dataColCount(idx);
          focusDataCell(idx, wantCol === Number.MAX_SAFE_INTEGER ? Math.max(0, cols - 1) : Math.min(wantCol, Math.max(0, cols - 1)));
        } else {
          focusGridRow(idx);
        }
      }
      return;
    }
    setActiveRow((i) => (rows.length === 0 ? -1 : i < 0 ? -1 : Math.min(i, rows.length - 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, gridNav]);

  // Auto-focus the FIRST row on open / after a genuine data reload — keyed on the
  // stable query `data` (NOT the per-render `rows` slice, which would loop; NOT
  // internal page changes, which keep the same `data`). Guards mirror AppDataGrid:
  // only CRUD lists, never while the detail view is up, never during the cross-module
  // stale-data window, never steal focus from a form field the user is in (a search
  // box), never disrupt a row already being navigated inside this table.
  useEffect(() => {
    if (!gridNav || detail || loadingThisModule) return;
    if (rows.length === 0) return;
    const ae = document.activeElement as HTMLElement | null;
    const onField =
      !!ae &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) ||
        ae.isContentEditable ||
        ae.getAttribute("role") === "combobox" ||
        ae.getAttribute("role") === "listbox");
    if (onField) return;
    if (ae && ae.closest?.(".mlv-tbl-scroll")) return; // already navigating this table
    const idx = activeRow >= 0 && activeRow < rows.length ? activeRow : 0;
    setActiveRow(idx);
    rowRefs.current[idx]?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, gridNav, detail, loadingThisModule]);

  // Form title must match the sidebar/navigation name (navData label).
  const title = navTitle ?? mod?.title ?? moduleKey;
  // Subgroup labels between "Settings" and this form (e.g. ['Analysis']) for the
  // breadcrumb, mirroring the sidebar tree (owner request 2026-10-14: show the
  // subgroup on all Settings forms). Only for Settings forms; a label equal to the
  // form title is dropped so a form whose subgroup shares its name (Default Settings)
  // does not read "… › Default Settings › Default Settings".
  const settingsSubs = underSettings ? navSubgroupPath(moduleKey).filter((s) => s !== title) : [];

  // Frozen (sticky-left) columns — cumulative left offset from their widths.
  // Keyed by the RENDERED (visible, reordered) column index so frozen offsets stay
  // correct after a hide / reorder / resize.
  const frozenLeft = useMemo(() => {
    const map: Record<number, number> = {};
    let acc = 0;
    cm.visibleColumns.forEach((c, idx) => {
      if (c.frozen) {
        map[idx] = acc;
        acc += cm.colWidth(c.key) ?? c.width ?? 150;
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cm.visibleColumns, cm.colWidth]);
  // Equal-width columns (user request 2026-09-11): when a compact settings list
  // gives EVERY visible column the SAME configured width (e.g. the ICM Utility
  // masters, all width 100) and there are no frozen/resized columns, render the
  // table with `table-layout: fixed` so those columns are EXACTLY equal instead
  // of auto-layout handing extra row width to whichever column has more content.
  // Scoped to small lists (2–6 cols, width ≤ 150) so wide data grids that rely on
  // content sizing + horizontal scroll are untouched.
  const equalCols = useMemo(() => {
    const vc = cm.visibleColumns;
    if (vc.length < 2 || vc.length > 6) return false;
    if (vc.some((c) => c.frozen)) return false;
    if (vc.some((c) => cm.colWidth(c.key) != null)) return false; // a manual drag overrides
    const widths = vc.map((c) => c.width ?? 0);
    const w0 = widths[0] ?? 0;
    return w0 > 0 && w0 <= 150 && widths.every((w) => w === w0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cm.visibleColumns, cm.colWidth]);
  // Fit EVERY column in the viewport (legacy XAF ListView): table-layout:fixed at
  // width:100%, configured widths act as proportions, headers/cells ellipsis-truncate.
  const fitAllColumns = crud?.fitAllColumns === true;
  // Module column width (Data Center = G5 layout, 2026-10-13): fixed-width
  // columns under table-layout:fixed so long values truncate instead of
  // widening the column; a wide set scrolls sideways, like the XAF ListView.
  const g5Cols = fixedColumnWidth !== undefined && !fitAllColumns;
  // The widths are emitted as PERCENTAGES of the configured total, never px.
  // `table-layout: fixed` does NOT scale px widths down to the table: the browser
  // honours them literally, so a column set summing wider than the container still
  // overflows — measured, 1452px of columns rendered a 1482px table inside a 1000px
  // box, which is why ICP-MS Mass Pick (1360px of columns) scrolled sideways while
  // its config claimed to fit. A percentage resolves against the table width, so the
  // columns scale down and keep their proportions. For a set that sums NARROWER than
  // the container the two are identical — the surplus is distributed proportionally
  // either way (measured, byte-for-byte) — so this changes nothing for the forms that
  // already opted in. (owner report 2026-09-25)
  const fitLeadCells = !fitAllColumns
    ? []
    : [
        ...(hasSelectCol ? [SELECT_COL_W] : []),
        ...(prospectsEditable ? [PENCIL_COL_W] : []),
        ...(crud && !crud.hideEditPencil && !crud.pureListView && perm.write ? [PENCIL_COL_W] : []),
        ...(detailCol ? [PENCIL_COL_W] : []),
      ];
  const fitWidthTotal = fitTotal(
    fitLeadCells,
    fitAllColumns ? cm.visibleColumns.map((c) => cm.colWidth(c.key) ?? c.width ?? FIT_FALLBACK_COL_W) : [],
  );
  /** One column's share of the table, as a CSS percentage. */
  const pctOf = (w: number): string => fitPct(w, fitWidthTotal);
  /** Per-cell style: the frozen-column sticky offset, plus any alignment the
   *  column itself declares (ModuleColumn.Align — e.g. a numeric ID right-aligned).
   *  The column is passed in rather than looked up by index, because `idx` indexes
   *  whichever list the caller is mapping (`cols` OR `cm.visibleColumns`) and those
   *  diverge once a column is hidden or reordered. Alignment rides along here
   *  because every header and body cell already spreads this style, so one place
   *  covers the whole grid. */
  const stickyStyle = (idx: number, header: boolean, col?: { align?: string | null }): CSSProperties | undefined => {
    const align = (col?.align ?? undefined) as CSSProperties["textAlign"] | undefined;
    const alignSty = align ? { textAlign: align } : undefined;
    if (!(idx in frozenLeft)) return alignSty;
    return {
      ...alignSty,
      position: "sticky",
      left: frozenLeft[idx],
      zIndex: header ? 4 : 1,
    };
  };

  // ── Grouped view (e.g. User Role Right → grouped by Role, GoLive style) ──
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) =>
    setOpenGroups((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  // Detail columns = everything except the grouped column (it becomes the header).
  const groupCols = useMemo(() => cols.filter((c) => c.key !== groupBy?.col), [cols, groupBy]);
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const m = new Map<string, Record<string, unknown>[]>();
    for (const r of rows) {
      const k = String((r as Record<string, unknown>)[groupBy.col] ?? "");
      const arr = m.get(k);
      if (arr) arr.push(r as Record<string, unknown>);
      else m.set(k, [r as Record<string, unknown>]);
    }
    // A "placeholder" row (e.g. a user with no permissions at all) has every
    // detail column blank — keep the group header but drop the empty row so the
    // header reads "(Count=0)" with nothing underneath, matching GoLive.
    return Array.from(m, ([key, rs]) => ({
      key,
      rows: rs.filter((r) =>
        groupCols.some((c) => {
          const v = r[c.key];
          return v !== null && v !== undefined && String(v).trim() !== "";
        }),
      ),
    }));
  }, [groupBy, groupCols, rows]);

  // ── Report preview (Custom Reports: ReportID link + G5 "…" Preview) ──
  // Asks the stored-report endpoint for a short-lived PREVIEW TICKET
  // (?deliver=token) and opens that real URL — the shared utils/reportPreview
  // contract. It used to open a `blob:` URL, which the browser's PDF viewer
  // cannot re-fetch (reload / Download fail, and the 60 s revoke could blank a
  // slow tab) — user report 2026-10-13: "preview needs to work".
  const [previewing, setPreviewing] = useState<string | null>(null);
  // `base` picks the document type: reports (Custom Reports) or invoices
  // (Data Center ▸ Invoices — the G5-stored invoice PDF). Both endpoints speak
  // the same ?deliver=token ticket contract.
  const openReport = async (reportId: string, base: "reports" | "invoices" = "reports") => {
    if (!reportId || previewing) return;
    setPreviewing(reportId);
    const w = window.open("", "_blank"); // open synchronously so it's not popup-blocked
    try {
      const res = await api.get(`/api/v1/${base}/${encodeURIComponent(reportId)}/pdf`, { params: PREVIEW_AS_TOKEN });
      const ticket = previewTicketOf(res.data);
      if (!ticket) throw new Error("no preview ticket");
      const url = previewUrl(ticket);
      if (w) {
        w.location.href = url;
      } else if (!window.open(url, "_blank")) {
        notify.warning("Allow pop-ups for this site to preview reports.");
      }
    } catch {
      if (w) w.close();
      notify.error(`${base === "invoices" ? "Invoice" : "Report"} "${reportId}" could not be loaded.`);
    } finally {
      setPreviewing(null);
    }
  };

  // Preview an instrument-certificate/attachment file inline (opens the PDF in a
  // new tab). Same FileData endpoint the detail child grid downloads from; opened
  // synchronously so the popup blocker doesn't stop it.
  const openInstrumentFile = async (base: string, oid: string, name: string) => {
    if (!oid) return;
    const w = window.open("", "_blank");
    try {
      const res = await api.get(`${base}/${oid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      if (w) w.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      if (w) w.close();
      notify.error(`"${name || "File"}" could not be loaded.`);
    }
  };

  // Download the Initialization Templates row's attached Excel file with its original
  // filename (legacy Attachment-column download link, owner report 2026-10-22).
  const downloadMdiAttachment = async (oid: string, name: string) => {
    if (!oid) return;
    try {
      const res = await api.get(`/api/v1/mdi-templates/file/${oid}/download`, { responseType: "blob" });
      downloadBlob(res.data as Blob, name || "attachment.xlsx");
    } catch {
      notify.error(`"${name || "File"}" could not be downloaded.`);
    }
  };

  // ── Export (shared full-format "Export to" — ALL 10 ALPACA formats) ──
  // This list is SERVER-paginated, so `rows` is only the visible page.  The
  // shared ExportMenu takes a rowsProvider that re-fetches EVERY filtered row
  // (huge pageSize) at export time, so "Export to" always exports the whole
  // filtered dataset, never just the current page (user request 2026-07-08).
  const exportMatrix = (list: readonly Record<string, unknown>[]) => ({
    headers: cols.map((c) => c.label),
    rows: list.map((r) => cols.map((c) => fmt(r[c.key], c.dateOnly, c.currency))),
  });
  const exportData: ExportData = {
    filename: moduleKey,
    ...exportMatrix(rows),
    rowsProvider: async () => {
      // Client-fuzzy modules: the full set is already loaded and fuzzy-filtered in
      // the browser, so export exactly what the user is seeing (ranked, filtered).
      if (fuzzySearching) return exportMatrix(fuzzyFiltered);
      // Fetch EVERY filtered row for the export. 10000 is the backend's hard
      // PageSize ceiling (GetModuleListQuery validator InclusiveBetween(1,10000));
      // a larger value 400s and breaks the export, so we request exactly the max
      // (covers every Data Center list). If a module ever exceeds 10000 filtered
      // rows the export takes the first 10000 in the current sort — flagged below.
      const resp = await fetchModuleRows(moduleKey, { ...listParams, page: 1, pageSize: 10000 });
      const all = resp.data ?? [];
      if ((resp.meta?.totalCount ?? all.length) > all.length) {
        notify.info(
          `Exporting the first ${all.length.toLocaleString()} of ` +
            `${(resp.meta?.totalCount ?? 0).toLocaleString()} rows (export limit).`,
        );
      }
      return exportMatrix(all);
    },
  };

  // ── "Query Samples" centered modal (ALPACA's Query button) ────────────────
  // Open the dialog, prefilling its multi-select picks from the applied column
  // filters and its date pairs from the applied ranges.
  const openQuery = () => {
    const sel: Record<string, string[]> = {};
    for (const f of queryFields) {
      const v = filters[f.key];
      if (v?.length) sel[f.key] = [...v];
    }
    // Monthly Summary Repository: default Month + Year to the CURRENT month/year
    // when not already filtered (ALPACA-LIMS parity — its Query opens pre-filled).
    // The Month column displays month NAMES, so seed the name.
    if (moduleKey === "msr") {
      const nowQ = new Date();
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthName = monthNames[nowQ.getMonth()] ?? "January";
      if (!sel.year?.length) sel.year = [String(nowQ.getFullYear())];
      if (!sel.month?.length) sel.month = [monthName];
    }
    setQSel(sel);
    setQForm({
      collectedFrom,
      collectedTo,
      receivedFrom: fromDate,
      receivedTo: toDate,
      analyzedFrom: analysisFrom,
      analyzedTo: analysisTo,
    });
    setQueryOpen(true);
  };

  // Apply the dialog: multi-select picks → per-column IN-list filters; date pairs → ranges.
  const applyQuery = () => {
    setFilters((prev) => {
      const next = { ...prev };
      for (const f of queryFields) {
        const v = qSel[f.key];
        if (v?.length) next[f.key] = v;
        else delete next[f.key];
      }
      return next;
    });
    setCollectedFrom(qForm.collectedFrom ?? "");
    setCollectedTo(qForm.collectedTo ?? "");
    setFromDate(qForm.receivedFrom ?? "");
    setToDate(qForm.receivedTo ?? "");
    setAnalysisFrom(qForm.analyzedFrom ?? "");
    setAnalysisTo(qForm.analyzedTo ?? "");
    setAppliedRange(rangeDays); // commit the (draft) Date Range too
    setQueryOpen(false);
    setPage(1);
  };

  // Refresh: commit the draft Date Range, reset to page 1, AND force a refetch
  // (invalidate the cached query) so it ALWAYS reloads — even when nothing
  // changed, a plain "Refresh" must re-pull from the server.
  const retrieve = () => {
    setAppliedRange(rangeDays);
    setPage(1);
    void refreshList();
  };

  const activeQueryCount =
    queryFields.filter((f) => (filters[f.key]?.length ?? 0) > 0).length +
    (analysisFrom || analysisTo ? 1 : 0) +
    (collectedFrom || collectedTo ? 1 : 0);

  // ── Load timer + top progress bar (intervals cleaned up → no leak) ──
  const [liveMs, setLiveMs] = useState(0);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const startRef = useRef<number | null>(null);
  const prevFetch = useRef(false);
  // Time from first render of this module to when its data is first available —
  // used when the grid is served from React Query's fresh cache (no network
  // fetch, so isFetching never flips). Without this the load-time chip would
  // vanish on a cache hit; other forms that set loadMs in their fetch .finally
  // always show a time, so ModuleListView must too. (2026-09-26)
  const mountRef = useRef(performance.now());
  useEffect(() => {
    if (isFetching && !prevFetch.current) {
      startRef.current = performance.now();
      setLiveMs(0);
    }
    if (!isFetching && prevFetch.current && startRef.current != null) {
      // A real fetch just finished — report its measured duration.
      setLoadMs(Math.round(performance.now() - startRef.current));
    } else if (!isFetching && loadMs === null && data !== undefined) {
      // Data came straight from the client cache (no fetch cycle) — still show a
      // time so the chip is consistent with every other form, never blank.
      setLoadMs(Math.round(performance.now() - mountRef.current));
    }
    prevFetch.current = isFetching;
  }, [isFetching, data, loadMs]);
  useEffect(() => {
    if (!isFetching) return;
    const id = window.setInterval(() => {
      if (startRef.current != null) setLiveMs(Math.round(performance.now() - startRef.current));
    }, 100);
    return () => window.clearInterval(id); // cleared on stop / unmount
  }, [isFetching]);

  const win = useMemo(() => {
    let s = Math.max(1, page - 2);
    const e = Math.min(totalPages, s + 4);
    s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }, [page, totalPages]);

  // Date-range label for the non-advanced native <select> chrome (draft = applied there).
  const rangeKey = RANGES.find((r) => r.days === rangeDays)?.label ?? "All";
  // Subtitle label reflects what's actually APPLIED: a custom Date Received range
  // (from the panel) wins, otherwise the committed preset label (e.g. "3M", "All").
  const appliedRangeKey = RANGES.find((r) => r.days === effectiveRange)?.label ?? "All";
  const dateRangeLabel =
    fromDate || toDate
      ? `${fromDate || "…"} → ${toDate || "…"}`
      : effectiveRange == null
        ? "All dates"
        : appliedRangeKey;

  // ── Full-page Settings Detail View (New / Edit) — replaces the list while
  // front (BO UI Standard §4 / CLAUDE.md §18.6).  Same shared component for
  // every CRUD-enabled settings form, driven by that module's MODULE_CRUD
  // config.  Prev/next walks the current page's ids.
  // Bespoke details that can't be a MODULE_CRUD field list still render FULL-PAGE
  // here, dispatched by moduleKey: 'msr' (Year/Month/Category + Retrieve + samples
  // grid), the Sample Field Config detail, and 'msr-data-import-config' (five
  // multi-select GUID pickers + ImportMode radio) — none are popups (standing rule 12).
  // `crud.dialogEdit` forms opt OUT of the full-page detail entirely — they keep
  // the list front and render <ModuleCrudDialog> over it (see the list return).
  // Phase 2.3 — the enhanced/MSR toolbar renders New/Edit/Delete/Query/Summary/
  // AutoGenerate as CUSTOM left-slot buttons (AppToolbar's onNew/onEdit/onDelete
  // are suppressed for msr), so they need explicit shortcuts (CLAUDE.md standing
  // rule 14, §18.6, ADR-0013).  MUST be above the detail early return (rules of
  // hooks); each mirrors its button's render gate + handler.  Non-msr forms get
  // New/Edit/Delete/Refresh/Export from the AppToolbar props (auto-bound) — this
  // only fills the custom-button gaps.
  const mlvKeys: HotkeyDef[] = [];
  if (moduleKey === "msr" && crud) {
    if (perm.create) mlvKeys.push({ combo: "alt+n", label: "New", group: title, order: 1, run: () => openNew() });
    if (perm.write)
      mlvKeys.push({
        combo: "alt+e",
        label: "Edit",
        group: title,
        order: 2,
        run: () => openEditSelected(),
        disabled: selected.size !== 1,
      });
    if (perm.delete)
      mlvKeys.push({
        combo: "alt+d",
        label: "Delete",
        group: title,
        order: 3,
        run: () => {
          void deleteSelected();
        },
        disabled: selected.size === 0 || bulkDeleting,
      });
    mlvKeys.push({ combo: "alt+m", label: "Summary", group: title, order: 5, run: () => setSummaryOpen(true) });
    mlvKeys.push({ combo: "alt+g", label: "AutoGenerate", group: title, order: 6, run: () => setAutoGenOpen(true) });
  }
  if (advanced) mlvKeys.push({ combo: "alt+q", label: "Query", group: title, order: 4, run: () => openQuery() });
  if (enhanced)
    mlvKeys.push({
      combo: "alt+r",
      label: "Refresh",
      group: title,
      order: 7,
      run: () => retrieve(),
      disabled: isFetching,
    });
  useHotkeys(mlvKeys);

  // A read-only reference list (detailReadOnly + NO REST basePath — e.g. Operators,
  // a static VALUES list with no controller of its own) has no GET base/{id}; its
  // full-page detail reads the record straight from the rows already loaded in the
  // list, so no backend endpoint is added and the static data is not duplicated.
  // Any future static reference list gets the same behaviour for free.
  const staticListAdapter = useMemo<DetailDataAdapter | undefined>(() => {
    if (!crud || !crud.detailReadOnly || crud.basePath) return undefined;
    const idField = crud.idField;
    return {
      load: async (rid: string) =>
        (rawRows as Record<string, unknown>[]).find((r) => String(r[idField] ?? "") === rid) ?? {},
      create: async () => {
        throw new Error("This reference list is read-only.");
      },
      update: async () => {
        throw new Error("This reference list is read-only.");
      },
    };
  }, [crud, rawRows]);

  // Default Pricing's New opens G5's bulk "New" list instead of an empty detail form.
  const defaultPricingNew = moduleKey === "set-test-default-pricing" && detail?.id === null;
  if (crud && detail && !crud.dialogEdit) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* List/Detail segmented switch — Detail active; List returns to the grid.
            Hidden for MSR: it moves its Load/Save/Cancel into the heading area. */}
        {/* Not over Default Pricing's New list: it is a list view in G5, not the record's detail. */}
        {moduleKey !== "msr" && !crud.hideViewToggle && !crud.plainList && !defaultPricingNew && (
          <div style={{ padding: "10px 16px 0", flex: "0 0 auto" }}>
            <ViewToggle
              view="detail"
              onChange={(v) => {
                if (v === "list") setDetail(null);
              }}
            />
          </div>
        )}
        {defaultPricingNew ? (
          // Default Pricing ▸ New → the G5 "New" list of unpriced tests (TestPriceSurcharge_ListView_newlist),
          // not the one-record form: Save stores the ticked rows, then back to this list (owner 2026-10-15).
          <DefaultPricingNewPage
            onHome={onHome}
            onClose={() => setDetail(null)}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
          />
        ) : moduleKey === "msr" ? (
          // MSR full-page inline detail (Retrieve/samples grid — can't be a
          // MODULE_CRUD field list) rendered Settings-style inside the tab.
          <MsrDetailDialog
            inline
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
            onNew={() => {
              setDetail(null);
              openNew();
            }}
          />
        ) : moduleKey === "set-dailyqc-logbook" ? (
          // Daily QC Log Book Builder — the owner prototype page (ALPACA_Daily_QC_Setup_v20):
          // Builder | Logbook Preview tabs, one Save for header + schedule + fields (2026-09-28).
          <DailyQcBuilderPage
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => void refreshList()}
            onClose={() => setDetail(null)}
          />
        ) : moduleKey === "set-flutter-template-builder" ? (
          // Flutter Template Builder — its OWN bespoke detail (ALPACA
          // FlutterTemplateBuilderController / SDATemplate_DetailView): header +
          // Visual Matrix + the Station/Sampling/Test field-assignment dual-list.
          <FlutterTemplateBuilderDetail
            id={detail.id}
            pageId={moduleKey}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : moduleKey === "set-sample-matrix-field-setup" ? (
          // Sample Matrix Field Setup — its OWN bespoke detail (ALPACA
          // VisualMatrix_DetailView_FieldSetup over dbo.SampleMatrixSetupFields):
          // a SINGLE panel with columns Name·Freeze·Width·Order·Caption. This is
          // distinct from Sample Field Configuration's 3-tab detail — the earlier
          // "reuse Sample Field Configuration detail" wiring (2026-09-26) rendered
          // the wrong screen (owner report 2026-09-29). §18.6 shared save flow.
          <SampleMatrixFieldSetupDetail
            id={detail.id}
            pageId={moduleKey}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : moduleKey === "set-samplefieldconfig" ? (
          // Sample Field Configuration — bespoke VisualMatrix field-config detail
          // (header + 3 tabs of available↔selected fields; §18.6 shared save flow).
          <SampleFieldConfigDetail
            id={detail.id}
            pageId={moduleKey}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : moduleKey === "msr-data-import-config" ? (
          // MSR Data Import Config — bespoke full-page detail (System + five
          // type-to-search multi-select GUID pickers + ImportMode radio; can't be
          // a MODULE_CRUD field list). Full-page, not a popup (standing rule 12).
          <MsrDataImportConfigDetail
            id={detail.id}
            initialMode={detail.mode}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : moduleKey === "set-mdi-templates" ? (
          // Initialization Templates — bespoke detail (header: MDI Version · MDI Name* · Attachment
          // Browse/Load/Reset; "Import Template Sheets" parses the Excel sheet names; editable
          // Templates child grid). The master-data import EXECUTION engine is deferred.
          <MdiTemplatesDetail
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : (
          <SettingsDetailPage
            key={detailNonce}
            config={crud}
            adapter={staticListAdapter}
            id={detail.id}
            initialMode={detail.mode}
            title={title}
            group={group}
            siblingIds={pageIds}
            onHome={onHome}
            onNavigate={(nid) => setDetail({ id: nid, mode: detail.mode })}
            onClose={() => setDetail(null)}
            onSaved={refreshList}
            /* Test Code -> "Copy from an existing test" (prototype v12). Only
               this key opts in, so every other form renders unchanged. The
               panel hides itself unless the record is NEW, and registers its
               payload with the Save flow so the create and the copy are ONE
               request - never a saved-but-empty test if the copy fails. */
            /* Rename Test / Copy Test / Copy Parameters - they need dialogs, which
               the config-driven recordActions cannot express, so they come through
               the shared recordActionsSlot. Saved records only. */
            /* The setup checklist sits directly under the title, above the
               action bar - the prototype order is Stepper, General Information,
               Method Defaults, Test Parameter Defaults. */
            detailClassName={moduleKey === "set-testcode" ? "tc-detail" : moduleKey === "crm-unfollowed-clients" ? "cuc-bigheads" : (moduleKey === "crm-prospects-open" || moduleKey === "crm-prospects-myopen" || moduleKey === "crm-prospects-closed" || moduleKey === "crm-unfollowed-prospects") ? "prospect-bigheads" : undefined}
            headerSlot={
              moduleKey === "set-testcode"
                ? (rid) => <TestCodeSetupStrip id={rid} />
                : undefined
            }
            recordActionsSlot={
              moduleKey === "set-testcode"
                ? (rid, viewOnly, reloadRecord) => (
                    <TestCodeRecordActions
                      id={rid}
                      viewOnly={viewOnly}
                      /* Both: the list row AND the open record. A rename changes
                         the record on screen, so refreshing only the list leaves
                         the title and the Test field on the old name. */
                      onChanged={() => { refreshList(); reloadRecord(); }}
                      onCopyTest={() => void openNewCopiedFrom(rid)}
                    />
                  )
                : undefined
            }
            /* Open / My Open Prospects — a "Close Prospects" button BEFORE Edit in the view bar
               (owner report 2026-10-28, image 1043). It reuses the list's close action config, so the
               detail button opens the SAME Status / Actual Revenue / Close Date popup and posts the one
               record's id to the close endpoint; on success the detail returns to the (refreshed) list,
               where the now-closed prospect no longer appears. Scoped to these two keys only. */
            recordActionsSlotBeforeEdit={
              (moduleKey === "crm-prospects-open" || moduleKey === "crm-prospects-myopen") && crud.basePath
                ? ((closeAction) =>
                    closeAction
                      ? (rid: string) => (
                          <ProspectCloseButton
                            basePath={crud.basePath!}
                            id={rid}
                            action={closeAction}
                            onClosed={() => { setDetail(null); void refreshList(); }}
                          />
                        )
                      : undefined)(crud.listActions?.find((a) => a.key === "close"))
                : undefined
            }
            newRecordDefaults={copySource?.defaults}
            bufferedChild={
              moduleKey === "set-testcode"
                ? (ctx) => <TestCodeCopyFromPanel ctx={ctx} prefill={copySource} />
                : undefined
            }
            // Every Settings ▸ Utility form reads square (de-yellow + square
            // corners) in its detail view, without tagging each moduleCrud entry
            // (owner request 2026-10-13). A form with its own squareControls is
            // unaffected (the prop is additive).
            forceSquareControls={isUtilityForm(moduleKey)}
          />
        )}

        {/* Copy Test on a GROUP test, from the DETAIL action bar. The detail
            view renders its own tree, so this popup has to be mounted here as
            well as with the list's popups — otherwise the click sets the state
            and nothing opens. */}
        {groupCopy && (
          <TestCodeCopyGroupDialog
            onClose={() => setGroupCopy(null)}
            onPick={takeGroupSource}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`bl-page mlv-host${fullGrid ? " mlv-fullgrid" : ""}`}>
      <div className={`mlv-progress${isFetching ? " on" : ""}`} />
      {listImportOpen && crud?.listImport && (
        <ImportFileDialog config={crud.listImport} onClose={() => setListImportOpen(false)} onImported={refreshList} />
      )}
      {/* Unfollowed Prospects ▸ Edit — the full "Open Prospect" form in EDIT mode (owner request
          2026-10-26, image 989). GETs/PUTs the prospect at /clients/lookups/source-leads/{id}. */}
      {prospectEditId && (
        <OpenProspectDialog
          editId={prospectEditId}
          getUrl="/api/v1/clients/lookups/source-leads"
          updateUrl="/api/v1/clients/lookups/source-leads"
          optionsUrl="/api/v1/customers/source-leads"
          onClose={() => setProspectEditId(null)}
          onSaved={() => { setProspectEditId(null); void refreshList(); }}
        />
      )}
      {/* Hidden file picker for the bulk Import (crud.bulkLoad lookups). */}
      {crud?.bulkLoad && (
        <input ref={importRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onImportFile} />
      )}
      {/* Items "Import File" (ALPACA parity) — its own picker, .xls/.xlsx like legacy. */}
      {moduleKey === "set-inventory-items" && (
        <input
          ref={itemsImportRef}
          type="file"
          accept=".xls,.xlsx"
          style={{ display: "none" }}
          onChange={onItemsImportFileChosen}
        />
      )}

      {/* ── Monthly Summary Repository — bespoke ALPACA-style dialogs ────────
          New/Edit (Year/Month/Category + Retrieve + samples grid), Summary
          (combined saved samples) and AutoGenerate (bulk headers per category).
          Merged team package 2026-07-02. */}
      {/* MSR (Monthly Summary Repository) New/Edit now renders FULL-PAGE inline
          in the List/Detail tab (see the early-return branch above) — no popup. */}
      {/* MSR Data Import Config New/Edit also renders FULL-PAGE inline
          (<MsrDataImportConfigDetail>) in the early-return branch above — the old
          popup was removed 2026-09-26 for the no-popup standard (standing rule 12). */}
      {/* Popup New/Edit — opt-in per form via MODULE_CRUD.dialogEdit (Settings ▸
          Permit Library New ▸ Permit Type New). The list stays front; this
          renders over it. Driven by the SAME MODULE_CRUD field list as the
          full-page detail, and saves through the shared useSaveFlow. */}
      {crud?.dialogEdit && detail && (
        moduleKey === "set-mdi-log" ? (
          // Initialization Log — the bespoke "Initialization Log Details" popup (legacy
          // InitializationLogDetail_DetailView). dialogEdit keeps the list front so the
          // popup overlays it instead of blanking the page (owner report 2026-10-01, image 308).
          <MdiLogDetail
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
            onClose={() => setDetail(null)}
          />
        ) : (
          <ModuleCrudDialog
            config={crud}
            id={detail.id}
            /* Row click opens 'view' (read-only, with an Edit button); the Edit
               pencil / toolbar Edit open 'edit'. New is always editable. */
            mode={detail.mode}
            onClose={() => setDetail(null)}
            onSaved={() => {
              setDetail(null);
              void refreshList();
            }}
          />
        )
      )}
      {moduleKey === "msr" && summaryOpen && <MsrSummaryDialog onClose={() => setSummaryOpen(false)} />}
      {moduleKey === "msr" && autoGenOpen && (
        <MsrAutoGenerateDialog
          onClose={() => setAutoGenOpen(false)}
          onDone={() => {
            setAutoGenOpen(false);
            void refreshList();
          }}
        />
      )}

      {/* ── "Query Samples" centered modal (ALPACA parity) ───────────────────── */}
      {advanced && queryOpen && (
        <div className="qs-overlay" onMouseDown={() => setQueryOpen(false)}>
          <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="qs-titlebar">
              <span className="qs-title">
                <i className="ti ti-filter" /> Query Panel
              </span>
              <div className="qs-titlebar-right">
                {/* Preset Date Range (same as the toolbar — committed on OK) */}
                <DateRangeSelect
                  variant="dropdown"
                  label="Date Range"
                  ranges={TRACKING_RANGES}
                  value={rangeDays}
                  onChange={setRangeDays}
                />
                <i className="ti ti-x qs-close" title="Close" onClick={() => setQueryOpen(false)} />
              </div>
            </div>
            <div className="qs-body">
              {/* Two balanced columns: left = first N combos; right = the
                  remaining combos then this module's date ranges. */}
              <div className="qs-grid">
                <div className="qs-col">
                  {queryFields.slice(0, queryLeftCount).map((f) => (
                    <div className="qs-field" key={f.key}>
                      <label className="qs-label">{f.label}</label>
                      <QueryCombo
                        moduleKey={moduleKey}
                        column={f.key}
                        values={qSel[f.key] ?? []}
                        placeholder={`Select ${f.label}…`}
                        cascade={qSel}
                        descending={f.key === "jobId"}
                        onChange={(v) => setQSel((s) => ({ ...s, [f.key]: v }))}
                      />
                    </div>
                  ))}
                </div>
                <div className="qs-col">
                  {queryFields.slice(queryLeftCount).map((f) => (
                    <div className="qs-field" key={f.key}>
                      <label className="qs-label">{f.label}</label>
                      <QueryCombo
                        moduleKey={moduleKey}
                        column={f.key}
                        values={qSel[f.key] ?? []}
                        placeholder={`Select ${f.label}…`}
                        cascade={qSel}
                        descending={f.key === "jobId"}
                        onChange={(v) => setQSel((s) => ({ ...s, [f.key]: v }))}
                      />
                    </div>
                  ))}
                  {queryDates.map((d) => (
                    <div className="qs-field qs-field-date" key={d.key}>
                      <label className="qs-label">{d.label}</label>
                      <div className="qs-date-pair">
                        <QueryDatePicker
                          ariaLabel={`${d.label} from`}
                          value={qForm[`${d.key}From`] ?? ""}
                          max={qForm[`${d.key}To`] || undefined}
                          onChange={(v) => setQForm((s) => ({ ...s, [`${d.key}From`]: v }))}
                        />
                        <span className="qs-date-sep">–</span>
                        <QueryDatePicker
                          ariaLabel={`${d.label} to`}
                          value={qForm[`${d.key}To`] ?? ""}
                          min={qForm[`${d.key}From`] || undefined}
                          onChange={(v) => setQForm((s) => ({ ...s, [`${d.key}To`]: v }))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="qs-footer">
              <div className="qs-footer-actions">
                <button className="ca-btn ca-btn-solid" onClick={applyQuery}>
                  <span aria-hidden="true">⬇️</span> Load
                </button>
                <button
                  className="ca-btn"
                  onClick={() => {
                    setQSel({});
                    setQForm({});
                  }}
                >
                  <i className="ti ti-filter-off" /> Clear all
                </button>
              </div>
              <span className="qs-footer-hint">
                Click <b>Load</b> to apply these filters.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Fixed top: breadcrumb / title / subtitle (same as every other form) ─ */}
      <div className="bl-top">
        {/* Settings CRUD list: the "Home › Settings › Form" breadcrumb sits ABOVE the
            List/Detail tabs (owner 2026-10-14, matching Exception Log). onHome, group
            ("Settings" via pageGroups) and title all come from the nav entry, so it
            can't drift from the sidebar. Non-Settings / non-CRUD forms don't hit this
            (non-CRUD already render the breadcrumb inside .dp-page-hdr below). */}
        {showCrudCrumb && (
          <div className="dp-breadcrumb" style={{ paddingTop: 12 }}>
            <a onClick={onHome} style={{ cursor: "pointer" }}>Home</a>
            <i className="ti ti-chevron-right" />
            <span>{group}</span>
            <i className="ti ti-chevron-right" />
            {settingsSubs.map((s) => (
              <Fragment key={s}>
                <span>{s}</span>
                <i className="ti ti-chevron-right" />
              </Fragment>
            ))}
            <span className="dp-bc-active">{title}</span>
          </div>
        )}
        {/* List/Detail segmented switch (BO UI Standard) — top-LEFT, exactly
            where the prototype places it.  CRUD BOs only; Detail opens the
            selected row (or the first row) in the shared SettingsDetailPage. */}
        {/* List/Detail toggle hidden for MSR (buttons live in the heading instead).
            Top padding tightens to 2px when the Settings breadcrumb sits above (it
            already supplies the top gap); otherwise the usual 10px below the top bar. */}
        {crud && moduleKey !== "msr" && !crud.hideViewToggle && !crud.plainList && !crud.pureListView && (
          <div style={{ padding: `${showCrudCrumb ? 2 : 10}px 0 0` }}>
            <ViewToggle
              view="list"
              detailDisabled={pageIds.length === 0}
              onChange={(v) => {
                if (v !== "detail") return;
                const id = selected.size === 1 ? Array.from(selected)[0]! : pageIds[0];
                if (id) setDetail({ id });
              }}
            />
          </div>
        )}
        {/* mlv-hdr--empty: a CRUD list (non-MSR, non-plainList) shows its title in the
            TOP BAR, suppresses the breadcrumb and hides the subtitle, so this header
            holds only the EMPTY FormTitle placeholder (which still publishes the
            load-time + Audit chips to the toolbar). Collapse its 26px padding so no
            blank strip sits between the List/Detail tabs and the action bar (owner
            request 2026-10-14 — Bottle Sharing / Contract Lab et al). */}
        <div className={`dp-page-hdr${crud?.searchInToolbar ? " mlv-hdr--tight" : ""}${
          (crud && moduleKey !== "msr" && !crud.plainList) || moduleKey === "edi-limits" ? " mlv-hdr--empty" : ""
        }`}>
          {/* In-page breadcrumb REMOVED for CRUD modules (user 2026-07-02) — the
              topbar already shows the path.  EXCEPTION: MSR shows it, matching the
              Custom Reporting list standard (shared `dp-breadcrumb` → standard
              muted-link + ink-active colours). (user request 2026-07-15) */}
          {/* edi-limits wraps ModuleListView inside LimitsPage, which already
              renders its own breadcrumb — so suppress this duplicate one there
              (user request 2026-09-19). */}
          {/* Never twice: a Settings CRUD list already shows showCrudCrumb above the tabs, so a
              plainList Settings form (Canceled Item Charge et al) must not add this second
              copy (owner report 2026-10-15: "why it shows duplicates"). */}
          {!showCrudCrumb && ((!crud && moduleKey !== "edi-limits") || moduleKey === "msr" || crud?.plainList) && (
            <div className="dp-breadcrumb">
              <a onClick={onHome} style={{ cursor: "pointer" }}>
                Home
              </a>
              <i className="ti ti-chevron-right" />
              <span>{group}</span>
              <i className="ti ti-chevron-right" />
              {settingsSubs.map((s) => (
                <Fragment key={s}>
                  <span>{s}</span>
                  <i className="ti ti-chevron-right" />
                </Fragment>
              ))}
              <span className="dp-bc-active">{title}</span>
            </div>
          )}
          {/* ONE heading only (user 2026-07-02: "too many heading") — the top
              title takes the FormTitle style (nav icon tile + 16px/800 text +
              the load-time chip); the old second strip below is gone. */}
          <div className="dp-title-row">
            <FormTitle
              pageId={moduleKey}
              title={title}
              loadMs={isFetching ? liveMs : loadMs}
              loading={isFetching}
              // No Audit chip for a reference list with no REST backend (basePath
              // "" — e.g. Operators): it is static data with no audit trail.
              // `auditFormName` opts a real-but-backend-less list back in, and lets a
              // screen point at the OWNING record's form so one lifecycle reads as one
              // trail (Retired Document → "Document Creator").
              auditFormName={crud?.auditFormName ?? (crud?.basePath ? crud.formName : undefined)}
            />
            {/* MSR "Load" — top-right of the title row (corner), matching the
                Custom Reporting list standard: opens the SELECTED record in its
                read-only detail view; enabled once exactly one row is ticked.
                (user request 2026-07-15) */}
            {moduleKey === "msr" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  className="ca-btn ca-btn-solid"
                  onClick={openEditSelected}
                  disabled={selected.size !== 1}
                  title={
                    selected.size === 1 ? "Load the selected Monthly Summary" : "Tick exactly one row to enable Load."
                  }
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
              </div>
            )}
          </div>
          {/* BO UI Standard v3 (2026-09-15) — clean chrome: this auto "Read-only ·
              N items · …" list caption is retired (it repeats the footer count).
              Kept in the tree but hidden via `.mlv-subcap` so every referenced
              value stays used (noUnusedLocals) and detail-page subtitles are
              untouched; delete fully when the vars are cleaned up. */}
          <div className="dp-subtitle mlv-subcap">
            {crud ? "" : "Read-only · "}
            {displayCount.toLocaleString()} items
            {mod?.hasDateRange ? ` · ${enhanced ? dateRangeLabel : rangeKey}` : ""}
            {advanced && activeQueryCount > 0
              ? ` · ${activeQueryCount} query filter${activeQueryCount > 1 ? "s" : ""}`
              : ""}
            {/* Descriptive tail (Custom Reporting subtitle style) — user request 2026-07-15. */}
            {moduleKey === "msr" ? " · Every monthly summary in the repository — list view" : ""}
            {!mapped && " · data source mapping in progress"}
          </div>
        </div>
      </div>

      {/* ── Grid area — toolbar / search sit ON the grey page (EXACT prototype
          layout); only the GRID below is the white card. The FormTitle now
          lives in the page header above (one heading only). */}
      <div className="bl-grid-area">
        {/* MSR list guidance hint — shared <StepHint> (BO UI Standard §5),
              info (blue) tone, directly under the title and above the toolbar.
              (user request 2026-07-15) */}
        {moduleKey === "msr" && (
          <StepHint tone="guidance">
            <b>Tick a report</b> to see what you can do with it — <b>Load</b> to view or edit it, or tick one or more to{" "}
            <b>Delete</b> them.
          </StepHint>
        )}

        {/* Toolbar on its OWN full-width row, left-aligned with Export pushed
              hard right (BO UI Standard §6 — the action bar is a separate row
              below the FormTitle, not squeezed onto the title row). */}
        <div className="mlv-toolbar-row">
          {/* Shared toolbar (CLAUDE.md §18.6) — canonical order:
                [left: Date-Range · Query] · New · Delete · Refresh · Export to.
                Audit is VIEW-ONLY and lives on the title row (FormTitle chip), not
                here (BO UI Standard v2, 2026-07-01).  Form-specific controls (the
                Date-Range preset and the "Query Samples" button) keep their existing
                order in the `left` slot; New/Delete are the standard CRUD props (only when this module
                is CRUD-enabled); the Export dropdown stays a self-anchored menu in
                the `right` slot so all three formats remain available. */}
          <AppToolbar
            crudFirst={!!crud?.crudFirst}
            right={crud?.searchInToolbar ? searchBox : undefined}
            /* Test Code bar order, matching the ALPACA/prototype v12 toolbar:
               Audit Trail · Import File · Template Download · New · Edit ·
               Delete · Set Category · Refresh · Export to. Both opt-ins are
               PROPS on the shared toolbar (the newFirst/editFirst/crudFirst
               precedent), so no other form moves and the buttons keep their
               shared style and auto-registered hotkeys. */
            auditFirst={isTestCode}
            /* ALPACA captions for this bar (owner 2026-10-13). Defaults stay
               'Import' / 'Template' for every other form. */
            importLabel={isTestCode ? "Import File" : undefined}
            templateLabel={isTestCode ? "Template Download" : undefined}
            importFirst={isTestCode}
            beforeRefresh={
              <>
                {isTestCode && (
                  <button
                    className="ca-btn"
                    onClick={() => setSetCatOpen(true)}
                    disabled={selected.size === 0}
                    title={selected.size === 0 ? "Tick one or more tests first" : "Set Category"}
                  >
                    <span className="tb-i">🏷</span> Set Category
                  </button>
                )}
                {/* Config-driven actions placed right AFTER Delete (crud.listActions placement
                    "beforeRefresh") — e.g. Open / My Open Prospects ▸ Close Prospects. */}
                {crud?.listActions?.filter((a) => a.placement === "beforeRefresh").map((a) => (
                  <button
                    key={a.key}
                    className="ca-btn"
                    onClick={() => void runListAction(a)}
                    disabled={!a.noSelectionMessage && selected.size === 0}
                    title={a.label}
                  >
                    <span className="tb-i tb-i-green">{a.icon ?? "⚙"}</span> {a.label}
                  </button>
                ))}
              </>
            }
            left={
              <>
                {/* Caller-injected form-specific actions (e.g. LimitsPage's Import Limits),
                    first in the left slot so they sit next to Refresh. */}
                {toolbarExtras}
                {/* Config-driven form actions (crud.listActions), e.g. Technical Writer ▸ Release.
                    "beforeRefresh" / "beforeExport" actions render in their own slots, not here. */}
                {crud?.listActions?.filter((a) => a.placement !== "beforeExport" && a.placement !== "beforeRefresh").map((a) => (
                  <button
                    key={a.key}
                    className="ca-btn"
                    onClick={() => void runListAction(a)}
                    disabled={!a.noSelectionMessage && selected.size === 0}
                    title={a.label}
                  >
                    <span className="tb-i tb-i-green">{a.icon ?? "⚙"}</span> {a.label}
                  </button>
                ))}
                {/* Canceled Item Charge ▸ Reactivate — un-cancels every ticked row (G5 toolbar "Reactivate"). */}
                {moduleKey === "set-itemcharge-canceled" && (
                  <button
                    className="ca-btn"
                    onClick={() => void doReactivateCharges()}
                    disabled={selected.size === 0}
                    title="Reactivate"
                  >
                    <span className="tb-i tb-i-green">↻</span> Reactivate
                  </button>
                )}
                {/* Retired Document ▸ Reactivate (un-retire) — legacy XAF toolbar
                    "Reactivate" (Retired Document list). Enabled when exactly one
                    row is checked; confirms, then the doc returns to Document Creator. */}
                {moduleKey === "set-retireddocument" && (
                  <button
                    className="ca-btn"
                    onClick={() => void doReactivate()}
                    disabled={selected.size !== 1}
                    title="Reactivate"
                  >
                    <span className="tb-i tb-i-green">↻</span> Reactivate
                  </button>
                )}
                {/* Preset Date Range — custom orange dropdown on enhanced forms
                    (selecting only stages the value; Refresh applies it). Other
                    forms keep the native auto-applying <select>. */}
                {/* §17 shared date-range chip row (1M·3M·6M·1Y·All, active = orange).
                    Enhanced forms also stage appliedRange; both apply immediately. */}
                {mod?.hasDateRange && enhanced && (
                  <DateRangeSelect
                    variant="dropdown"
                    label="Date Range"
                    ranges={TRACKING_RANGES}
                    value={rangeDays}
                    onChange={(d) => {
                      setRangeDays(d);
                      setAppliedRange(d);
                      setPage(1);
                    }}
                  />
                )}
                {mod?.hasDateRange && !enhanced && (
                  <DateRangeSelect
                    variant="dropdown"
                    label="Date Range"
                    ranges={TRACKING_RANGES}
                    value={rangeDays}
                    onChange={(d) => {
                      setRangeDays(d);
                      setPage(1);
                    }}
                  />
                )}
                {/* MSR (user request 2026-07-09): standard New · Edit · Delete sit
                    RIGHT AFTER the Date Range and BEFORE the form-specific
                    Query / Summary / AutoGenerate.  Rendered inline here (and
                    suppressed on AppToolbar via onNew/onEdit/onDelete=undefined for
                    msr) because AppToolbar always draws its New/Edit/Delete AFTER
                    the whole left slot. */}
                {moduleKey === "msr" && crud && (
                  <>
                    {/* Gated by the user's Module Access Control level for MSR
                        (MonthlySummaryRepository_ListView): New=Create, Edit=Write,
                        Delete=Delete. Hidden when the MAC doesn't grant them. */}
                    {perm.create && (
                      <button className="ca-btn" onClick={openNew} title="New">
                        <span className="tb-i tb-i-green">＋</span> New
                      </button>
                    )}
                    {perm.write && (
                      <button className="ca-btn" onClick={openEditSelected} disabled={selected.size !== 1} title="Edit">
                        <span className="tb-i tb-i-blue">✎</span> Edit
                      </button>
                    )}
                    {perm.delete && (
                      <button
                        className="ca-btn"
                        onClick={deleteSelected}
                        disabled={selected.size === 0 || bulkDeleting}
                        title="Delete"
                      >
                        <span className="tb-i tb-i-red">🗑</span> Delete
                      </button>
                    )}
                  </>
                )}
                {/* Items — inline New · Edit · Delete · Import File · Export items stock,
                    the SAME left-to-right order as legacy's Items_ListView toolbar (user
                    request 2026-09-23: "the order for the action should be same in the
                    intelligence also"). AppToolbar's own New/Edit/Delete are suppressed
                    above (moduleKey !== "set-inventory-items") so they render exactly once,
                    here, ahead of the two form-specific actions instead of after them.
                    "Export items stock" is legacy's own name for what is actually the
                    import TEMPLATE download, not a live data export — see the backend note.
                    Refresh and Export to still follow, unchanged, as AppToolbar's own
                    (rendered after this whole left slot). */}
                {moduleKey === "set-inventory-items" && crud && (
                  <>
                    <button type="button" className="ca-btn" onClick={openNew} title={hotkeyTip("New", "alt+n")}>
                      <span className="tb-i tb-i-green">＋</span> New
                    </button>
                    <button
                      type="button"
                      className="ca-btn"
                      onClick={openEditSelected}
                      disabled={selected.size !== 1}
                      title={hotkeyTip("Edit", "alt+e")}
                    >
                      <span className="tb-i tb-i-blue">✎</span> Edit
                    </button>
                    <button
                      type="button"
                      className="ca-btn"
                      onClick={deleteSelected}
                      disabled={selected.size === 0 || bulkDeleting}
                      title={hotkeyTip("Delete", "alt+d")}
                    >
                      <span className="tb-i tb-i-red">🗑</span> Delete
                    </button>
                    <button
                      type="button"
                      className="ca-btn"
                      onClick={pickItemsImportFile}
                      title={hotkeyTip("Import File", "alt+i")}
                    >
                      <i className="ti ti-upload" /> Import File
                    </button>
                    {/* Stock Qty Edit (ALPACA StockQtyEdit action, between Import File and Export items
                        stock): opens the Stock Qty Editor over the ticked items. G5 shows it only to an
                        administrative role or a role with Items Write — the MAC write right here. */}
                    <button
                      type="button"
                      className="ca-btn"
                      disabled={!perm.write || selected.size === 0}
                      onClick={() => setStockQtyEditIds(Array.from(selected))}
                      title={selected.size === 0 ? "Stock Qty Edit — Requires at least one object selected" : "Stock Qty Edit"}
                    >
                      <span className="tb-i tb-i-blue">✎</span> Stock Qty Edit
                    </button>
                    <button
                      type="button"
                      className="ca-btn"
                      onClick={() => void exportItemsStockTemplate()}
                      title="Export items stock"
                    >
                      <i className="ti ti-download" /> Export items stock
                    </button>
                  </>
                )}
                {/* "Query Samples" button — opens the centered modal */}
                {advanced && (
                  <button className="ca-btn rs-btn-query" onClick={openQuery}>
                    <i className="ti ti-filter" /> Query
                  </button>
                )}
                {/* MSR "Summary" — combined saved samples across MSRs (ALPACA parity) */}
                {moduleKey === "msr" && (
                  <button className="ca-btn" onClick={() => setSummaryOpen(true)} title="Summary">
                    <i className="ti ti-table" /> Summary
                  </button>
                )}
                {/* MSR "AutoGenerate" — create headers for all categories (ALPACA parity) */}
                {moduleKey === "msr" && (
                  <button className="ca-btn" onClick={() => setAutoGenOpen(true)} title="AutoGenerate">
                    <i className="ti ti-sparkles" /> AutoGenerate
                  </button>
                )}
                {/* MSR "Export To XLSX" / "Export To CSV" — standalone buttons matching
                    legacy ALPACA's SelectAndExportMSRSamplesInxlsx_Execute /
                    SelectAndExportMSRSamplesIncsv_Execute (ALPACA parity 2026-07-10).
                    Exports the SAMPLE-LEVEL rows (chemistry data) for the selected MSR(s),
                    NOT the MSR header grid — identical to the ALPACA LIMS behaviour.
                    Filename: MSRSamples_YYYYMMDDHHMMSS.xlsx / .csv */}
                {moduleKey === "msr" && (
                  <>
                    <button
                      className="ca-btn"
                      title="Export To XLSX"
                      disabled={selected.size === 0}
                      onClick={() => void msrExportSamples("xlsx", selected, notify)}
                    >
                      <i className="ti ti-file-type-xls" /> Export To XLSX
                    </button>
                    <button
                      className="ca-btn"
                      title="Export To CSV"
                      disabled={selected.size === 0}
                      onClick={() => void msrExportSamples("csv", selected, notify)}
                    >
                      <i className="ti ti-file-type-csv" /> Export To CSV
                    </button>
                  </>
                )}
                {/* Refresh — coloured to match the Query/Export buttons on the
                    enhanced toolbar (the plain AppToolbar Refresh is used on
                    every other module).  Always force-reloads via `retrieve`. */}
                {enhanced && (
                  <button className="ca-btn rs-btn-refresh" onClick={retrieve} disabled={isFetching} title="Refresh">
                    {isFetching ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>}{" "}
                    Refresh
                  </button>
                )}
              </>
            }
            newLabel="New"
            // MSR and Items render New/Edit/Delete inline in the left slot — MSR
            // before Query/Summary/AutoGenerate (user request 2026-07-09), Items so
            // New·Edit·Delete·Import File·Export items stock lands in the SAME
            // legacy order (user request 2026-09-23) — suppress the built-in ones
            // so they aren't drawn twice.
            // Sample Field Configuration is Edit-only (ALPACA parity: the list
            // hides New + Delete — a matrix's field config is edited, never
            // created/deleted from here).
            // MAC gate (perm = useNavActions(moduleKey), fail-open for admins / no
            // MAC): New needs Create, Edit needs Write, Delete needs Delete. Only
            // tightens where a MAC level restricts this form's view; every other
            // module is unchanged (perm.* = true).
            onNew={
              crud &&
              !crud.readOnlyList &&
              !crud.detailReadOnly &&
              !crud.editOnlyList &&
              !crud.hideNewButton &&
              perm.create &&
              moduleKey !== "msr" &&
              moduleKey !== "set-samplefieldconfig" &&
              moduleKey !== "set-sample-matrix-field-setup" &&
              moduleKey !== "set-inventory-items"
                ? openNew
                : undefined
            }
            onEdit={
              crud &&
              !crud.readOnlyList &&
              !crud.detailReadOnly &&
              !crud.hideEditButton &&
              perm.write &&
              moduleKey !== "msr" &&
              moduleKey !== "set-inventory-items"
                ? openEditSelected
                : undefined
            }
            editDisabled={selected.size !== 1}
            onDelete={
              crud &&
              !crud.readOnlyList &&
              !crud.detailReadOnly &&
              !crud.editOnlyList &&
              !crud.hideDeleteButton &&
              perm.delete &&
              moduleKey !== "msr" &&
              moduleKey !== "set-samplefieldconfig" &&
              moduleKey !== "set-sample-matrix-field-setup" &&
              moduleKey !== "set-inventory-items"
                ? deleteSelected
                : undefined
            }
            deleteDisabled={selected.size === 0 || bulkDeleting}
            onRefresh={enhanced ? undefined : retrieve}
            refreshing={isFetching}
            // Import + Template show only on lists that opt in with bulkLoad (Method, G5 parity).
            onImport={crud?.bulkLoad ? pickImportFile : undefined}
            onTemplate={crud?.bulkLoad ? downloadTemplate : undefined}
            beforeExport={crud?.listImport || toolbarExtrasAfterRefresh || crud?.listActions?.some((a) => a.placement === "beforeExport") ? (
              <>
                {toolbarExtrasAfterRefresh}
                {/* Config-driven actions placed after Refresh (crud.listActions placement
                    "beforeExport"), e.g. the G5 Instrument barcode buttons. */}
                {crud?.listActions?.filter((a) => a.placement === "beforeExport").map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className="ca-btn"
                    onClick={() => void runListAction(a)}
                    disabled={!a.noSelectionMessage && selected.size === 0}
                    title={a.label}
                  >
                    <span className="tb-i tb-i-green">{a.icon ?? "⚙"}</span> {a.label}
                  </button>
                ))}
                {crud?.listImport && (
                  <>
                    <button type="button" className="ca-btn" onClick={() => setListImportOpen(true)} title="Import File">
                      <i className="ti ti-upload" /> Import File
                    </button>
                    {crud.listImport.templateUrl && (
                      <button type="button" className="ca-btn" onClick={() => void downloadListImportTemplate()} title="Template Download">
                        <i className="ti ti-download" /> Template Download
                      </button>
                    )}
                  </>
                )}
              </>
            ) : undefined}
            exportData={exportData}
          />
        </div>

        {/* Category chips - filter the list to ONE category. Server-side, so a
            chip matches across every page AND can match one item of the
            "; "-joined category list rather than the whole string. Test Code
            only; renders nothing for any other module. */}
        {isTestCode && (
          <TestCodeCategoryChips
            cats={testCodeCats.cats}
            none={testCodeCats.none}
            /* the UNFILTERED total - displayCount is the filtered count, which
               made the All chip read 1 while a chip was active */
            total={testCodeCats.total}
            value={category}
            onChange={(v) => {
              setCategory(v);
              setPage(1);
            }}
          />
        )}
        {isTestCode && (
          <TestCodeSetCategoryDialog
            open={setCatOpen}
            ids={[...selected].map(String)}
            cats={testCodeCats.cats}
            onClose={() => setSetCatOpen(false)}
            onDone={() => {
              testCodeCats.reload();
              void refreshList();
            }}
          />
        )}

        {/* Related-lookups family strip (BO UI Standard §4) — HIDDEN for now
              (user 2026-07-02: "set visible false … I will tell later").
              Re-enable by removing the `false &&`. */}
        {false && crud?.relatedLookups && (
          <RelatedLookups
            items={crud!.relatedLookups!}
            activeId={moduleKey}
            onSelect={(id) => {
              if (id !== moduleKey) notify.info("This form isn’t available yet.");
            }}
          />
        )}

        {scopeTabs && (
          <div style={{ margin: "0 0 8px" }}>
            <style>{`
              .mlv-scope-tabs { display: inline-flex; border: 1px solid var(--ai-line); border-radius: 8px; overflow: hidden; background: #fff; }
              .mlv-scope-tabs button { border: 0; background: #fff; padding: 7px 14px; font-size: 12.5px; font-weight: 700; color: #55607a; cursor: pointer; font-family: inherit; }
              .mlv-scope-tabs button + button { border-left: 1px solid var(--ai-line); }
              .mlv-scope-tabs button.on { background: var(--brand-accent, var(--ai-orange)); color: #fff; }
            `}</style>
            <span className="mlv-scope-tabs" role="tablist" aria-label="Data set">
              {scopeTabs.tabs.map((t, i) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={i === scopeTab}
                  className={i === scopeTab ? "on" : ""}
                  onClick={() => {
                    setScopeTab(i);
                    setPage(1);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </span>
          </div>
        )}

        {/* Global search — OUTSIDE the grid card, on the grey page, styled
              exactly like the toolbar buttons (user 2026-07-02). */}
        <div className={`list-search-row${crud?.searchInToolbar ? " mlv-search-row--bar" : ""}`}>
          {/* On the action bar instead when the module opts in (searchInToolbar); this row then
              collapses and its Export / Maximize icons sit at the right end of the action bar. */}
          {/* Maximized: the action bar is hidden, so this row comes back as a plain row holding the
              Export / Restore icons. The search stays with the action bar and is not repeated here. */}
          {!crud?.searchInToolbar && searchBox}
          {/* Export + Maximize icon controls — right end of the search row. */}
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" {...exportData} />
            <button
              className={`dv-gridact${fullGrid ? " dv-on" : ""}`}
              onClick={() => setFullGrid((v) => (v ? null : true))}
              title={fullGrid ? "Restore" : "Maximize"}
            >
              <i className={`ti ti-arrows-${fullGrid ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>

        {/* Grid — the ONE white card on the grey page (EXACT prototype .grid). */}
        <div className="bl-card bl-card-flex">
          <div
            className={`bl-tbl-wrap mlv-tbl-scroll${kbdActive ? " mlv-kbd" : ""}`}
            onMouseDownCapture={kbdActive ? () => setKbdActive(false) : undefined}
          >
            {loadingThisModule ? (
              // First load: show ONLY a loading indicator — no placeholder text.
              <div className="bl-empty" style={{ flexDirection: "column", gap: 8 }}>
                <LoaderSymbol size={26} />
                <div style={{ color: "#666" }}>Loading…</div>
              </div>
            ) : !mapped ? (
              // Not yet data-mapped: still render the column headers so the grid
              // keeps its standard layout when there is no data, with a single
              // info row in place of the rows (mirrors GoLive's empty grids).
              <table className="bl-tbl mlv-table">
                <thead>
                  <tr>
                    {cols.map((c, idx) => {
                      const sty = stickyStyle(idx, true, c);
                      return (
                        <th
                          key={c.key}
                          className={c.frozen ? "mlv-frozen" : undefined}
                          style={{ ...(c.width ? { minWidth: c.width } : {}), ...(sty ?? {}) }}
                        >
                          <div className="col-hdr-cell">
                            <span className="col-hdr-label">{c.label}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {!isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, cols.length)}>No data to display — data source mapping in progress.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : isError ? (
              // 404 / UNKNOWN_MODULE = this nav item has no ModuleRegistry data
              // source (global ListView fallback) — show an empty list view, not
              // a hard error.  Any other failure is a real error.
              (() => {
                const e = error as { response?: { status?: number; data?: { error?: { code?: string } } } } | null;
                const notConfigured =
                  e?.response?.status === 404 || e?.response?.data?.error?.code === "UNKNOWN_MODULE";
                // A real load failure must NOT wipe the whole grid (columns +
                // headers) — if we already know the columns (from a prior load or
                // the cached module meta), keep the header row and show the error
                // as a single in-grid row, so a transient/filtered failure never
                // makes "all the data AND columns disappear" (user report
                // 2026-07-08).  Only the genuine "not configured" 404 stays a
                // full empty-state card.
                if (notConfigured) {
                  return (
                    <div className="bl-empty" style={{ color: "#64748b", flexDirection: "column", gap: 6 }}>
                      <i className="ti ti-list-details" style={{ fontSize: 24, opacity: 0.35 }} />
                      <div>This form is not configured with a data source yet.</div>
                    </div>
                  );
                }
                if (cols.length === 0) {
                  return (
                    <div className="bl-empty" style={{ color: "#b91c1c", flexDirection: "column", gap: 6 }}>
                      <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: 0.35 }} />
                      <div>Failed to load data.</div>
                    </div>
                  );
                }
                return (
                  <table className="bl-tbl mlv-table">
                    <thead>
                      <tr>
                        {cols.map((c, idx) => {
                          const sty = stickyStyle(idx, true, c);
                          return (
                            <th
                              key={c.key}
                              className={c.frozen ? "mlv-frozen" : undefined}
                              style={{ ...(c.width ? { minWidth: c.width } : {}), ...(sty ?? {}) }}
                            >
                              <div className="col-hdr-cell">
                                <span className="col-hdr-label">{c.label}</span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="mlv-nodata-row">
                        <td colSpan={Math.max(1, cols.length)} style={{ color: "#b91c1c" }}>
                          <i className="ti ti-alert-triangle" style={{ marginRight: 6, opacity: 0.6 }} />
                          Failed to load data — adjust the filter or click Refresh.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()
            ) : groupBy && grouped ? (
              <table className="bl-tbl mlv-table mlv-grouped">
                <thead>
                  <tr>
                    {groupCols.map((c) => (
                      <th key={c.key} style={c.width ? { minWidth: c.width } : undefined}>
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label">{c.label}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => {
                    const open = openGroups.has(g.key);
                    return (
                      <Fragment key={g.key || "(blank)"}>
                        <tr className="mlv-group-row" onClick={() => toggleGroup(g.key)}>
                          <td colSpan={groupCols.length}>
                            <i
                              className={`ti ti-${open ? "square-minus" : "square-plus"}`}
                              style={{ marginRight: 6, verticalAlign: "middle" }}
                            />
                            {groupBy.prefix}
                            {g.key} (Count={g.rows.length.toLocaleString()})
                          </td>
                        </tr>
                        {open &&
                          g.rows.map((row, i) => (
                            <tr key={`${g.key}-${i}`}>
                              {groupCols.map((c) => {
                                const v = fmt(row[c.key], c.dateOnly, c.currency);
                                // Boolean permission columns render as the shared
                                // .cellcheck — GREEN filled ✓ when enabled (BO UI
                                // Standard prototype; user 2026-07-02).  Skipped for
                                // text-enum columns (FORCE_TEXT_COLS) so tri-state
                                // enums like Default (Yes/No/N/A) stay text.
                                if ((v === "Yes" || v === "No") && !FORCE_TEXT_COLS.has(c.key)) {
                                  return (
                                    <td key={c.key} style={{ textAlign: "center" }}>
                                      <span
                                        className={`cellcheck${v === "Yes" ? " on" : ""}`}
                                        role="img"
                                        aria-label={v === "Yes" ? "Enabled" : "Disabled"}
                                      />
                                    </td>
                                  );
                                }
                                const gStatus = statusClass(v);
                                if (gStatus) {
                                  return (
                                    <td key={c.key}>
                                      <span className={gStatus}>{v}</span>
                                    </td>
                                  );
                                }
                                return (
                                  <td key={c.key} title={v}>
                                    {v}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                  {grouped.length === 0 && !isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, groupCols.length)}>No data to display</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table
                className={`bl-tbl mlv-table${equalCols || fitAllColumns || g5Cols ? " mlv-fixed" : ""}${fitAllColumns ? " mlv-fitall" : ""}`}
              >
                <thead>
                  <tr>
                    {hasSelectCol && (
                      <th
                        className="mlv-cb-cell"
                        style={{ width: fitAllColumns ? pctOf(SELECT_COL_W) : SELECT_COL_W, textAlign: "center" }}
                        // The whole cell is the click target, not just the 13px box
                        // (a hit area that small is hard to aim at). A click on the
                        // checkbox itself is handled by its own onChange.
                        onClick={(e) => {
                          if (e.target === e.currentTarget) toggleAll(!pageAllSelected);
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select all on page"
                          checked={pageAllSelected}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </th>
                    )}
                    {/* Unfollowed Prospects ▸ per-row Edit pencil (col 1). Blank header. */}
                    {prospectsEditable && (
                      <th
                        style={{ width: fitAllColumns ? pctOf(PENCIL_COL_W) : PENCIL_COL_W, textAlign: "center" }}
                        aria-label="Edit"
                      >
                        <div className="col-hdr-cell"><span className="col-hdr-label" /></div>
                      </th>
                    )}
                    {/* Retired Document ▸ per-row Reactivate (↻) leading column — legacy
                        XAF green-arrow cell before Category (image 212). Blank header. */}
                    {(moduleKey === "set-retireddocument" || moduleKey === "set-itemcharge-canceled") && (
                      <th
                        style={{ width: fitAllColumns ? pctOf(PENCIL_COL_W) : PENCIL_COL_W, textAlign: "center" }}
                        aria-label="Reactivate"
                      >
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label" />
                        </div>
                      </th>
                    )}
                    {/* Per-row Edit pencil (ALPACA parity). Header caption BLANK.
                          Hidden when the form opts in via hideEditPencil (Turn Around
                          Time matches the ALPACA list: checkbox, no pencil). */}
                    {crud && !crud.hideEditPencil && !crud.pureListView && perm.write && (
                      <th
                        style={{ width: fitAllColumns ? pctOf(PENCIL_COL_W) : PENCIL_COL_W, textAlign: "center" }}
                        aria-label="Edit"
                      >
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label" />
                        </div>
                      </th>
                    )}
                    {detailCol && (
                      <th
                        style={{ width: fitAllColumns ? pctOf(PENCIL_COL_W) : PENCIL_COL_W, textAlign: "center" }}
                        aria-label="Open detail"
                      >
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label" />
                        </div>
                      </th>
                    )}
                    {cm.visibleColumns.map((c, idx) => {
                      const sty = stickyStyle(idx, true, c);
                      const w = cm.colWidth(c.key);
                      return (
                        <th
                          key={c.key}
                          className={`${c.frozen ? "mlv-frozen" : ""}${cm.dragClass(c.key)}`.trim() || undefined}
                          // equalCols: leave width AUTO — under table-layout:fixed the auto
                          // columns divide the row EQUALLY (no explicit width to under/over-fill).
                          style={
                            g5Cols
                              ? { width: w ?? c.width ?? fixedColumnWidth, ...(sty ?? {}) }
                              : equalCols
                              ? (sty ?? undefined)
                              : fitAllColumns
                                ? // fixed layout: a PERCENTAGE of the table (see fitPct), NO
                                  // minWidth so a long header can't force the column wider.
                                  { width: pctOf(w ?? c.width ?? FIT_FALLBACK_COL_W), ...(sty ?? {}) }
                                : { minWidth: w ?? c.width ?? undefined, ...(w ? { width: w } : {}), ...(sty ?? {}) }
                          }
                          onContextMenu={(e) => openHeaderMenu(e, { field: c.key, label: c.label })}
                          {...cm.dragProps(c.key)}
                        >
                          <div className="col-hdr-cell">
                            <span
                              className="col-hdr-label mlv-sortable"
                              onClick={() => onSort(c.key)}
                              title={`Sort by ${c.label}`}
                            >
                              {c.label}
                            </span>
                            {/* UI-3: sort arrow at the column's right end (light grey, not bold).
                              Glyphs match the shared <SortableHeader>/<AppDataGrid> standard:
                              ⇅ unsorted · ▲ asc · ▼ desc (user request 2026-07-15). */}
                            <span
                              className={`mlv-sort-ind${shownSortCol === c.key ? " active" : ""}`}
                              onClick={() => onSort(c.key)}
                              aria-label={shownSortCol === c.key ? `sorted ${shownSortDesc ? "desc" : "asc"}` : "sort"}
                            >
                              {shownSortCol === c.key ? (shownSortDesc ? "▼" : "▲") : "⇅"}
                            </span>
                            {/* The Data Package Review Checklist Test column renders
                              "View Test List" links, not filterable values — hide
                              its column filter (user request 2026-08-26). */}
                            {!(moduleKey === "data-package-checklist" && c.key === "test") && (
                              <ColumnFilter
                                moduleKey={moduleKey}
                                column={c.key}
                                label={c.label}
                                rangeDays={mod?.hasDateRange ? effectiveRange : null}
                                selected={filters[c.key] ?? []}
                                cascade={filters}
                                sortDir={sortCol === c.key ? (sortDesc ? "desc" : "asc") : filterDefaultDir}
                                onChange={(v) => setColFilter(c.key, v)}
                              />
                            )}
                          </div>
                          {/* Resize handle (shared .adg-resize) — every column resizable. */}
                          <span
                            className="adg-resize"
                            title="Drag to resize"
                            onMouseDown={(e) => cm.startResize(c.key, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    // Stable key: CRUD grids key by the row id so checkbox/edit
                    // state stays bound to the right record under sort/filter
                    // (§18.6 — no array-index keys for interactive rows).
                    const rowKey = crud ? String(row[crud.idField] ?? i) : i;
                    return (
                      // Hover warms this record so the click opens data that is
                      // already in hand (see prefetchRow).
                      <tr
                        key={rowKey}
                        ref={
                          gridNav
                            ? (el) => {
                                rowRefs.current[i] = el;
                              }
                            : undefined
                        }
                        className={gridNav ? "mlv-row mlv-nav" : undefined}
                        tabIndex={rowTabIndex(i)}
                        onKeyDown={gridNav ? (e) => onRowKeyDown(e, i, row) : undefined}
                        onFocus={gridNav ? () => setActiveRow(i) : undefined}
                        onMouseEnter={() => prefetchRow(row)}
                        // Opt-in (crud.rowDoubleClickView): double-click the row opens
                        // its detail (read-only view), the AppDataGrid convention. Inline
                        // cell editing is disabled on these lists, so no cell-edit conflict.
                        onDoubleClick={crud?.rowDoubleClickView ? () => openView(row) : undefined}
                        style={crud?.rowDoubleClickView ? { cursor: "pointer" } : undefined}
                      >
                        {hasSelectCol && (
                          <td
                            className="mlv-cb-cell"
                            style={{ textAlign: "center", width: 40 }}
                            // Whole cell toggles the row (see the header cell above).
                            // stopPropagation: a click in the select column is never
                            // a "click the row" gesture.
                            onClick={(e) => {
                              e.stopPropagation();
                              if (e.target === e.currentTarget) toggleRow(String(row[selIdField] ?? ""));
                            }}
                          >
                            <input
                              type="checkbox"
                              aria-label="Select row"
                              checked={selected.has(String(row[selIdField] ?? ""))}
                              onChange={() => toggleRow(String(row[selIdField] ?? ""))}
                            />
                          </td>
                        )}
                        {/* Unfollowed Prospects ▸ per-row Edit pencil — opens the "Open Prospect"
                            form in EDIT mode for this prospect (stopPropagation: never a row click). */}
                        {prospectsEditable && (
                          <td className="mlv-actcell" style={{ width: 40, textAlign: "center" }}>
                            <button
                              type="button"
                              className="mlv-rowact"
                              title="Edit"
                              aria-label="Edit"
                              onClick={(e) => { e.stopPropagation(); setProspectEditId(String(row[selIdField] ?? "")); }}
                            >
                              <i className="ti ti-edit" aria-hidden="true" />
                            </button>
                          </td>
                        )}
                        {/* Retired Document ▸ per-row Reactivate (↻) — legacy XAF green
                            up-arrow cell before Category (image 212). Confirms, then
                            un-retires that row directly (stopPropagation so it never
                            opens the row's detail). */}
                        {/* Canceled Item Charge ▸ per-row Reactivate (green ↻) — the G5 row icon; un-cancels
                            that row (stopPropagation so it never opens a detail). */}
                        {moduleKey === "set-itemcharge-canceled" && (
                          <td className="mlv-actcell" style={{ width: 52, textAlign: "center", whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              className="mlv-rowact"
                              title="Reactivate"
                              aria-label="Reactivate"
                              style={{ color: "#1a7f37" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                void doReactivateCharges(String(row[crud?.idField ?? "oid"] ?? ""));
                              }}
                            >
                              {/* G5 glyph: a green ring with a green up-arrow inside. */}
                              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                                <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                                <path d="M8 4.5 L11.2 10.2 L4.8 10.2 Z" fill="currentColor" />
                              </svg>
                            </button>
                          </td>
                        )}
                        {moduleKey === "set-retireddocument" && (
                          <td className="mlv-actcell" style={{ width: 52, textAlign: "center", whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              className="mlv-rowact"
                              title="Reactivate"
                              aria-label="Reactivate"
                              style={{ color: "#1a7f37" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                void doReactivate(String(row[crud?.idField ?? "oid"] ?? ""));
                              }}
                            >
                              <i className="ti ti-circle-arrow-up" />
                            </button>
                          </td>
                        )}
                        {/* Per-row Edit pencil — hidden when the form sets hideEditPencil.
                            When crud.pencilRowEdit is on, the row being edited shows a
                            Save / Cancel pair instead (legacy XAF row editor); other rows
                            keep the pencil, which opens the row editor rather than the detail. */}
                        {crud && !crud.hideEditPencil && !crud.pureListView && perm.write && (
                          <td className="mlv-actcell" style={{ width: 52, whiteSpace: "nowrap" }}>
                            {rowEditable && editRowId === String(row[crud.idField] ?? "") ? (
                              <>
                                <button
                                  title="Save"
                                  disabled={savingCell}
                                  onClick={() => void saveRow()}
                                  className="mlv-rowact"
                                  style={{ color: "#1a7f37" }}
                                >
                                  <i className="ti ti-device-floppy" />
                                </button>
                                <button
                                  title="Cancel"
                                  disabled={savingCell}
                                  onClick={cancelRowEdit}
                                  className="mlv-rowact"
                                  style={{ color: "#b42318" }}
                                >
                                  <i className="ti ti-arrow-back-up" />
                                </button>
                              </>
                            ) : (
                              <button
                                title="Edit"
                                onClick={(e) => {
                                  // Stop the click reaching the row, which would open the detail view.
                                  e.stopPropagation();
                                  if (rowEditable) startRowEdit(row);
                                  else openEdit(row);
                                }}
                                className="mlv-rowact"
                                // G5 Method: the row-editor pencil is yellow and the open-detail icon is blue
                                // (owner request 2026-10-14). Same two-icon look for the Instrument list.
                                style={{ color: rowEditable && crud?.detailIcon ? "#d4a017" : "#1F4E79" }}
                              >
                                {/* The row editor's icon is the plain pencil (as on Instrument), so it reads differently from
                                    the edit icon that opens the detail — on every row-editor form, Method included. */}
                                <i className={`ti ${detailCol || rowEditable ? "ti-pencil" : "ti-edit"}`} />
                              </button>
                            )}
                            {/* Second row icon (G5 Method list): opens the full detail form. */}
                            {crud.detailIcon && !detailCol && !(rowEditable && editRowId === String(row[crud.idField] ?? "")) && (
                              <button
                                title="Edit"
                                onClick={() => openEdit(row)}
                                className="mlv-rowact"
                                style={{ color: "#1F4E79" }}
                              >
                                <i className="ti ti-edit" />
                              </button>
                            )}
                          </td>
                        )}
                        {/* G5 Instrument: second icon column — opens the full detail in edit mode. */}
                        {detailCol && (
                          <td className="mlv-actcell" style={{ width: PENCIL_COL_W, textAlign: "center" }}>
                            {!(rowEditable && editRowId === String(row[crud.idField] ?? "")) && (
                              <button
                                title="Open detail"
                                onClick={(e) => {
                                  // Stop the click reaching the row, which would open the read-only view.
                                  e.stopPropagation();
                                  openEdit(row);
                                }}
                                className="mlv-rowact"
                                style={{ color: "#1F4E79" }}
                              >
                                <i className="ti ti-edit" />
                              </button>
                            )}
                          </td>
                        )}
                        {cm.visibleColumns.map((c, idx) => {
                          const sty = stickyStyle(idx, false, c);
                          if (c.key === "preview") {
                            const rid = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {rid && (
                                  <button
                                    className="mlv-preview-btn"
                                    title={`Preview ${rid}`}
                                    disabled={previewing === rid}
                                    onClick={() => openReport(rid)}
                                  >
                                    {previewing === rid ? "…" : <i className="ti ti-file-type-pdf" />}
                                  </button>
                                )}
                              </td>
                            );
                          }
                          // Assign Items To Test: double-clicking the "Item" cell opens
                          // the AssignItemsDialog (every Item, the test's items ticked).
                          if (moduleKey === "set-assign-items-to-test" && c.key === "itemName") {
                            const label = String(row[c.key] ?? "");
                            const tmOid = String(row["oid"] ?? row["Oid"] ?? "");
                            const testCode = String(row["testCode"] ?? row["TestCode"] ?? "");
                            const testName = String(row["testName"] ?? row["TestName"] ?? "");
                            return (
                              <td
                                key={c.key}
                                className={c.frozen ? "mlv-frozen" : undefined}
                                style={sty}
                                onDoubleClick={() =>
                                  tmOid && setAssignItemsPopup({ testMethodOid: tmOid, testCode, testName })
                                }
                                title="Double-click to assign items"
                              >
                                {label}
                              </td>
                            );
                          }
                          // Report Recipient View: the Report / Invoice / Sample Receipt
                          // columns are "View Recipients" LINKS opening the per-client, per-kind
                          // recipient popup. The recipient COUNT is not shown in the link text
                          // (owner: "View (26)" → "View Recipient" [2026-10-15] → "View" → "View
                          // Recipient" → "View Recipients" [2026-10-16, image 712]).
                          if (
                            moduleKey === "set-report-recipient-view" &&
                            (c.key === "reportRecipients" || c.key === "invoiceRecipients" || c.key === "sampleReceiptRecipients")
                          ) {
                            const kind: RecipientKind =
                              c.key === "reportRecipients" ? "report" : c.key === "invoiceRecipients" ? "invoice" : "sampleReceipt";
                            const kindLabel = c.key === "reportRecipients" ? "Report" : c.key === "invoiceRecipients" ? "Invoice" : "Sample Receipt";
                            const clientOid = String(row["oid"] ?? row["Oid"] ?? "");
                            const clientName = String(row["client"] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                <button
                                  className="mlv-link-btn mlv-link-blue"
                                  title={`View ${kindLabel.toLowerCase()} recipients`}
                                  onClick={() =>
                                    clientOid && setRecipientPopup({ clientId: clientOid, clientName, kind, kindLabel })
                                  }
                                >
                                  View Recipients
                                </button>
                              </td>
                            );
                          }
                          // Technical Writer (legacy HelpCenter FAQ list, G5 HelpCenter_ListView_FAQ_Articles): the Topic
                          // column is a hyperlink — underlined G5 "Blue" (#0000FF) — and a click opens the article,
                          // as a click on its row does (owner request 2026-10-14).
                          if (moduleKey === "set-technical-writer" && c.key === "topic") {
                            const topic = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                <button
                                  type="button"
                                  className="mlv-link-btn mlv-link-blue"
                                  style={{ color: "#0000ff", textDecoration: "underline", textAlign: "left" }}
                                  title={`Open ${topic}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openView(row);
                                  }}
                                >
                                  {topic}
                                </button>
                              </td>
                            );
                          }
                          // Data Package Review Checklist: the Test column is a
                          // "View Test List" LINK opening the linked-tests popup
                          // (Test · Method), matching the legacy list view.
                          // The legacy `TestHyperLink` is an UNCONDITIONAL controller
                          // action — it renders on EVERY row regardless of whether the
                          // check point has tests assigned. A no-test row opens an empty
                          // ("No data to display") popup. Match that: show the link on
                          // every row, never blank the cell (owner report 2026-10-12 —
                          // the link was missing on the 21 rows with no assigned tests).
                          if (moduleKey === "data-package-checklist" && c.key === "test") {
                            const tests = String(row[c.key] ?? "").trim();
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                <button
                                  className="mlv-link-btn mlv-link-blue"
                                  title="View Test List"
                                  onClick={() => setLinkedTests({ label: String(row["checkPoint"] ?? ""), tests })}
                                >
                                  View Test List
                                </button>
                              </td>
                            );
                          }
                          // Custom Reports: clicking the ReportID opens the report
                          // PDF (mirrors GoLive, where ReportID is the link).
                          if (c.key === "reportId") {
                            const rid = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {rid ? (
                                  <button
                                    className="mlv-link-btn mlv-link-red"
                                    title={`Open report ${rid}`}
                                    disabled={previewing === rid}
                                    onClick={() => openReport(rid)}
                                  >
                                    {previewing === rid ? "…" : rid}
                                  </button>
                                ) : (
                                  ""
                                )}
                              </td>
                            );
                          }
                          // Custom Reports "Preview" — G5's "…" cell (ReportManagementPreviewDC)
                          // opens the same stored report PDF as the ReportID link.
                          // Data Center ▸ Invoices has the same G5 "…" (InvoicePreviewDC).
                          if ((moduleKey === "dc-reporting" || moduleKey === "dc-invoicing") && c.key === "preview") {
                            const isInvoice = moduleKey === "dc-invoicing";
                            const rid = String(row[isInvoice ? "invoiceId" : "reportId"] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={{ ...(sty ?? {}), textAlign: "center" }}>
                                {rid ? (
                                  <button
                                    className="mlv-link-btn mlv-link-red"
                                    style={{ textDecoration: "underline" }}
                                    title={`Preview ${isInvoice ? "invoice" : "report"} ${rid}`}
                                    disabled={previewing === rid}
                                    onClick={() => openReport(rid, isInvoice ? "invoices" : "reports")}
                                  >
                                    ...
                                  </button>
                                ) : (
                                  ""
                                )}
                              </td>
                            );
                          }
                          // Instruments Field / Lab / Sample Prep — clicking the
                          // "(n) Instruments" count cell opens the two-panel picker.
                          if (
                            (moduleKey === "set-instruments-field" ||
                              moduleKey === "set-instruments-lab" ||
                              moduleKey === "set-instruments-sampleprep") &&
                            c.key === "instrument"
                          ) {
                            const linkTable =
                              moduleKey === "set-instruments-sampleprep"
                                ? "sampleprep"
                                : moduleKey === "set-instruments-lab"
                                  ? "lab"
                                  : "field";
                            const oid = String(row.oid ?? row.Oid ?? "");
                            const label = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {oid ? (
                                  <button
                                    className="mlv-link-btn mlv-link-blue"
                                    style={{ textDecoration: "underline" }}
                                    title="Click to assign instruments"
                                    onClick={() =>
                                      setInstrumentPickerPopup({
                                        testMethodOid: oid,
                                        // row columns: matrix · test · method · instrumentAnalysis · instrument
                                        testCode: String(row.method ?? ""),
                                        testName: String(row.test ?? ""),
                                        linkTable,
                                      })
                                    }
                                  >
                                    {label}
                                  </button>
                                ) : (
                                  label
                                )}
                              </td>
                            );
                          }
                          // Instrument Certificate view: the Attachment cell PREVIEWS the
                          // file (opens the PDF in a new tab) — the row's companion
                          // attachmentOid is the FileData Oid, the cell text its filename.
                          if (moduleKey === "set-labware-certificate" && c.key === "attachment") {
                            const foid = String(row.attachmentOid ?? "");
                            const fname = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {foid ? (
                                  <button
                                    className="mlv-link-btn mlv-link-blue"
                                    style={{ textDecoration: "underline" }}
                                    title={`Preview ${fname || "file"}`}
                                    onClick={() =>
                                      void openInstrumentFile("/api/v1/labware-certificates/file", foid, fname)
                                    }
                                  >
                                    {fname || "View"}
                                  </button>
                                ) : (
                                  ""
                                )}
                              </td>
                            );
                          }
                          // Initialization Templates list: the Attachment cell is a DOWNLOAD
                          // link for the attached Excel file (legacy parity, owner report
                          // 2026-10-22). Companion attachmentOid = the FileData Oid.
                          if (moduleKey === "set-mdi-templates" && c.key === "attachment") {
                            const foid = String(row.attachmentOid ?? "");
                            const fname = String(row[c.key] ?? "");
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {foid && fname ? (
                                  <button
                                    className="mlv-link-btn mlv-link-blue"
                                    style={{ textDecoration: "underline" }}
                                    title={`Download ${fname}`}
                                    onClick={() => void downloadMdiAttachment(foid, fname)}
                                  >
                                    {fname}
                                  </button>
                                ) : (
                                  fname
                                )}
                              </td>
                            );
                          }
                          const val = fmt(row[c.key], c.dateOnly, c.currency);
                          // Settings ▸ Permit Library New ▸ Operators — the two reference
                          // columns render as chips, matching the Lifecycle v5 prototype:
                          // Display is the symbol itself, so it gets a monospace chip that
                          // reads as a token rather than prose; Kind is a small colour-coded
                          // badge so the three families separate at a glance.
                          //
                          // Scoped to this module ON PURPOSE. Keying on the bare column
                          // names 'display' / 'kind' would restyle any other grid that ever
                          // had a column so named; a reference list's presentation must not
                          // leak into unrelated forms.
                          //
                          // Both class names contain "chip"/"badge" DELIBERATELY —
                          // theme/blackText.css forces --ai-ink on everything inside a td
                          // except [class*="pill"|"chip"|"badge"|"status"], so a rename
                          // silently reverts them to plain black text.
                          if (moduleKey === "pln-operators" && (c.key === "display" || c.key === "kind")) {
                            const cls =
                              c.key === "display"
                                ? "mlv-opchip"
                                : `mlv-kind-badge ${OPERATOR_KIND_CLASS[val] ?? ""}`.trim();
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {val ? <span className={cls}>{val}</span> : ""}
                              </td>
                            );
                          }
                          // QA/QC ▸ Internal Audit Status: the shared status pill — Closed is green
                          // (ok), every Pending… state is amber (warn), as Sampling Allocation's
                          // Pending Sampling / Submitted (owner request 2026-10-27). The pill class
                          // contains "pill", so theme/blackText.css leaves its colour alone.
                          if (INTERNAL_AUDIT_CRUMB_KEYS.has(moduleKey) && c.key === "status") {
                            const variant = /^closed$/i.test(val.trim()) ? "ok" : /^pending/i.test(val.trim()) ? "warn" : statusVariant(val);
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                {val ? <span className={`adg-pill ${variant}`}>{val}</span> : ""}
                              </td>
                            );
                          }
                          // Boolean columns (Active, IsGroup, …) render as the
                          // shared .cellcheck — GREEN filled ✓ when enabled (BO UI
                          // Standard prototype; user 2026-07-02).  Skipped for
                          // text-enum columns (FORCE_TEXT_COLS) so tri-state enums like
                          // Default (Yes/No/N/A) render the literal word, matching legacy.
                          if ((val === "Yes" || val === "No") && !FORCE_TEXT_COLS.has(c.key)) {
                            return (
                              <td
                                key={c.key}
                                className={c.frozen ? "mlv-frozen" : undefined}
                                style={{ ...(sty ?? {}), textAlign: "center" }}
                              >
                                <span
                                  className={`cellcheck${val === "Yes" ? " on" : ""}`}
                                  role="img"
                                  aria-label={val === "Yes" ? "Enabled" : "Disabled"}
                                />
                              </td>
                            );
                          }
                          // Lifecycle-status text renders as coloured text — see
                          // statusClass() for the vocabulary and colours. §18.6 keeps every
                          // other cell on the single ink token; a status value is the
                          // sanctioned exception (same rule as statusPill).
                          // The class MUST contain "status": theme/blackText.css forces
                          // `color: var(--ai-ink) !important` on every element inside a td
                          // except those matching [class*="status" i] / pill / chip / badge.
                          const statusCls = statusClass(val);
                          if (statusCls) {
                            return (
                              <td key={c.key} className={c.frozen ? "mlv-frozen" : undefined} style={sty}>
                                <span className={statusCls}>{val}</span>
                              </td>
                            );
                          }
                          const itype = inlineType(c.key);
                          const rid = crud ? String(row[crud.idField] ?? "") : "";
                          // Per-cell double-click edit is suppressed on pencilRowEdit forms
                          // (the pencil drives the whole-row editor instead).
                          const editingThis =
                            !rowEditable && !!itype && !!editCell && editCell.id === rid && editCell.key === c.key;
                          const rowEditingThis = rowEditable && !!itype && editRowId === rid;
                          const tdCls =
                            [
                              c.frozen ? "mlv-frozen" : "",
                              isIdColumn(c.key, c.label) ? "mlv-id" : "",
                              itype ? "mlv-editable" : "",
                            ]
                              .filter(Boolean)
                              .join(" ") || undefined;
                          if (rowEditingThis) {
                            // Legacy XAF row editor — every inline-editable cell in the
                            // row edits at once; Save/Cancel live in the pencil column.
                            const efld = crudFieldByKey[c.key];
                            const selOpts = itype === "select" ? (efld?.options ?? inlineSelOpts[c.key] ?? []) : [];
                            const setRD = (v: string) => setRowDraft((d) => ({ ...d, [c.key]: v }));
                            return (
                              <td key={c.key} className={tdCls} style={sty}>
                                {itype === "multiselect" ? (
                                  // Click the box → the checkbox popup (G5 Parameter popup).
                                  <button
                                    type="button"
                                    className="mlv-inline-edit"
                                    style={{ textAlign: "left", width: "100%" }}
                                    disabled={savingCell}
                                    onClick={() => openMultiPopup(rid, c.key, efld)}
                                  >
                                    {multiCellLabel(c.key, rowDraft[c.key], row[c.key])}
                                  </button>
                                ) : itype === "select" && efld?.quickAdd?.welded ? (
                                  // Welded ＋ (Method Category, owner request 2026-10-14): the same control as the
                                  // detail form — the dropdown, the green ＋ and the caret in ONE box.
                                  <AiCombo
                                    className="mlv-welded"
                                    value={rowDraft[c.key] ?? null}
                                    options={selOpts.map((o) => ({ value: o.id, label: o.name }))}
                                    onChange={(v) => setRD(v ?? "")}
                                    onNew={() => setQuickAddKey(c.key)}
                                    newTitle={`Add ${efld.quickAdd.title}`}
                                    placeholder={efld.noneLabel ?? "N/A"}
                                    naLabel={efld.noneLabel ?? "N/A"}
                                    disabled={savingCell}
                                    compact
                                  />
                                ) : itype === "select" ? (
                                  <span className="sd-select-qa">
                                  <select
                                    className="mlv-inline-edit"
                                    style={{ flex: "1 1 auto", minWidth: 0 }}
                                    value={rowDraft[c.key] ?? ""}
                                    disabled={savingCell}
                                    onChange={(e) => setRD(e.target.value)}
                                  >
                                    {!efld?.hideBlankOption && <option value="">{efld?.noneLabel ?? "N/A"}</option>}
                                    {selOpts.map((o) => (
                                      <option key={o.id} value={o.id}>
                                        {o.name}
                                      </option>
                                    ))}
                                  </select>
                                  {/* "+" beside the dropdown (G5 / detail form): adds a new option inline. */}
                                  {efld?.quickAdd && !savingCell && (
                                    <button
                                      type="button"
                                      className="sd-qa-btn"
                                      title={`Add ${efld.quickAdd.title}`}
                                      aria-label={`Add ${efld.quickAdd.title}`}
                                      onClick={() => setQuickAddKey(c.key)}
                                    >
                                      <i className="ti ti-plus" aria-hidden="true" />
                                    </button>
                                  )}
                                  </span>
                                ) : itype === "textarea" ? (
                                  <textarea
                                    className="mlv-inline-edit"
                                    rows={2}
                                    value={rowDraft[c.key] ?? ""}
                                    disabled={savingCell}
                                    maxLength={efld?.maxLength}
                                    onChange={(e) => setRD(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelRowEdit();
                                      }
                                    }}
                                  />
                                ) : (
                                  <input
                                    className="mlv-inline-edit"
                                    type={itype === "date" ? "date" : "text"}
                                    value={rowDraft[c.key] ?? ""}
                                    disabled={savingCell}
                                    maxLength={efld?.maxLength}
                                    onChange={(e) => setRD(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        void saveRow();
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelRowEdit();
                                      }
                                    }}
                                  />
                                )}
                              </td>
                            );
                          }
                          if (editingThis) {
                            // UI-8: in-cell editor — commits on Enter/blur, cancels on Escape.
                            const efld = crudFieldByKey[c.key];
                            const selOpts = itype === "select" ? (efld?.options ?? inlineSelOpts[c.key] ?? []) : [];
                            return (
                              <td key={c.key} className={tdCls} style={sty}>
                                {itype === "select" ? (
                                  // Dropdown cell — pick commits immediately (legacy combo), with the
                                  // fresh value passed so it doesn't race setEditDraft. FK selects offer
                                  // a leading blank (noneLabel, e.g. N/A) unless hideBlankOption.
                                  <select
                                    autoFocus
                                    className="mlv-inline-edit"
                                    value={editDraft}
                                    disabled={savingCell}
                                    onChange={(e) => {
                                      setEditDraft(e.target.value);
                                      void commitEdit(e.target.value);
                                    }}
                                    onBlur={() => cancelEdit()}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelEdit();
                                      }
                                    }}
                                  >
                                    {!efld?.hideBlankOption && <option value="">{efld?.noneLabel ?? "N/A"}</option>}
                                    {selOpts.map((o) => (
                                      <option key={o.id} value={o.id}>
                                        {o.name}
                                      </option>
                                    ))}
                                  </select>
                                ) : itype === "textarea" ? (
                                  <textarea
                                    autoFocus
                                    className="mlv-inline-edit"
                                    rows={2}
                                    value={editDraft}
                                    disabled={savingCell}
                                    maxLength={crudFieldByKey[c.key]?.maxLength}
                                    onChange={(e) => setEditDraft(e.target.value)}
                                    onBlur={() => {
                                      void commitEdit();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        void commitEdit();
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelEdit();
                                      }
                                    }}
                                  />
                                ) : (
                                  <input
                                    autoFocus
                                    className="mlv-inline-edit"
                                    type={itype === "date" ? "date" : "text"}
                                    value={editDraft}
                                    disabled={savingCell}
                                    maxLength={crudFieldByKey[c.key]?.maxLength}
                                    onChange={(e) => setEditDraft(e.target.value)}
                                    onBlur={() => {
                                      void commitEdit();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        void commitEdit();
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelEdit();
                                      }
                                    }}
                                  />
                                )}
                              </td>
                            );
                          }
                          // Row-click opens the record READ-ONLY (view): for
                          // non-inline CRUD forms, and for pencilRowEdit forms (legacy
                          // XAF: clicking a row opens the detail, the pencil edits the
                          // row in place). Only the per-cell double-click forms keep a
                          // bare row-click. A cell being row-edited renders an editor
                          // (earlier branch), so this handler is never attached to it.
                          // readOnlyList (e.g. the Instrument Certificate view) is a
                          // pure view — no row-click detail; the Attachment link is the
                          // only interaction. Otherwise row-click opens the detail.
                          const rowClickView =
                            !crud?.readOnlyList &&
                            !crud?.plainList &&
                            (crud?.inlineRowEdit === false || crud?.pencilRowEdit === true);
                          // A select cell prefills from the companion `${key}Id` value
                          // (the FK Oid / enum int carried on the row) so the dropdown
                          // starts on the current option, not the resolved display name.
                          const editSeed = itype === "select" ? (row[`${c.key}Id`] ?? row[c.key]) : row[c.key];
                          // On pencilRowEdit forms the pencil is the only edit entry —
                          // no per-cell double-click hint or handler. rowDoubleClickView
                          // also turns inline editing off (the row's double-click opens
                          // the detail instead), so a cell double-click never edits in place.
                          const cellEditable = !!itype && !rowEditable && !crud?.rowDoubleClickView;
                          return (
                            <td
                              key={c.key}
                              title={cellEditable ? "Double-click to edit" : val}
                              className={tdCls}
                              style={rowClickView ? { ...(sty ?? {}), cursor: "pointer" } : sty}
                              onClick={rowClickView ? () => openView(row) : undefined}
                              onDoubleClick={cellEditable ? () => startEdit(rid, c.key, editSeed) : undefined}
                            >
                              {val}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {rows.length === 0 && !isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, cols.length + (crud ? (crud.hideEditPencil ? 1 : 2) + (detailCol ? 1 : 0) : selectableList ? 1 : 0))}>
                        No data to display
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {isFetching && !loadingThisModule && (
              <div className="bl-loading">
                <LoaderSymbol size={16} /> Loading…
              </div>
            )}
            {/* Header right-click menu (Sort · Hide · Show All · Column Chooser) +
                the Column Chooser popup — both portal to <body>; rendered once. */}
            {headerMenu}
            {cm.chooser}
          </div>

          {/* Pagination — same chrome as every other form */}
          {mapped && !groupBy && (
            <div className="bl-pgbar">
              <span className="pgb-info">
                Page {displayPage} of {totalPages} ({displayCount.toLocaleString()} {displayCount === 1 ? "item" : "items"})
              </span>
              <div className="pgb-nav">
                <button className="pgb-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <i className="ti ti-chevron-left" />
                </button>
                {win[0]! > 1 && (
                  <button className="pgb-btn" onClick={() => setPage(1)}>
                    1
                  </button>
                )}
                {win[0]! > 2 && <span className="pgb-ellipsis">…</span>}
                {win.map((n) => (
                  <button key={n} className={`pgb-btn${n === page ? " pgb-active" : ""}`} onClick={() => setPage(n)}>
                    {n}
                  </button>
                ))}
                {win[win.length - 1]! < totalPages && (
                  <button className="pgb-btn" onClick={() => setPage(totalPages)}>
                    {totalPages}
                  </button>
                )}
                <button
                  className="pgb-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <i className="ti ti-chevron-right" />
                </button>
              </div>
              <div className="pgb-size">
                <span className="pgb-size-lbl">Page size:</span>
                <select
                  className="pgb-size-sel"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {pageSizeLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Package Review Checklist — "View Test List" popup (Test · Method). */}
      {linkedTests && <LinkedTestsModal tests={linkedTests.tests} onClose={() => setLinkedTests(null)} />}
      {/* A list action that picks a value first (Close Prospects → Won / Cancelled). */}
      {choiceAction && (
        <ListActionChoiceDialog
          action={choiceAction}
          onClose={() => setChoiceAction(null)}
          onConfirm={(v, extras) => runChoiceAction(choiceAction, v, extras)}
        />
      )}
      {/* Quick-add popup opened from the "+" beside a row-edit dropdown. */}
      {quickAddKey && crudFieldByKey[quickAddKey]?.quickAdd && (
        <QuickAddDialog
          spec={crudFieldByKey[quickAddKey]!.quickAdd!}
          optionsUrl={crudFieldByKey[quickAddKey]!.optionsUrl ?? ""}
          onClose={() => setQuickAddKey(null)}
          onAdded={(opts, selectedId) => {
            const key = quickAddKey;
            setInlineSelOpts((s) => ({ ...s, [key]: opts }));
            if (selectedId) setRowDraft((d) => ({ ...d, [key]: selectedId }));
            setQuickAddKey(null);
          }}
        />
      )}
      {/* Checkbox popup opened from a multi-select cell in edit mode (G5 Parameter popup). */}
      {multiPopup && (
        <ParameterPickerDialog
          title={multiPopup.title}
          optionsUrl={multiPopup.url}
          initialPicked={multiPopup.initial}
          onClose={() => setMultiPopup(null)}
          onApply={(ids, names) => {
            const key = multiPopup.key;
            setMultiNames((m) => ({ ...m, [key]: { ...(m[key] ?? {}), ...names } }));
            setRowDraft((d) => ({ ...d, [key]: ids.join(";") }));
            setMultiPopup(null);
          }}
        />
      )}
      {/* Items ▸ Stock Qty Edit — the Stock Qty Editor popup. */}
      {stockQtyEditIds && (
        <StockQtyEditDialog
          itemIds={stockQtyEditIds}
          onClose={() => setStockQtyEditIds(null)}
          onSaved={() => {
            setStockQtyEditIds(null);
            setSelected(new Set());
            void refreshList();
          }}
        />
      )}
      {/* Assign Items To Test — item-picker popup (shared AppDialog + AppDataGrid). */}
      {assignItemsPopup && (
        <AssignItemsDialog
          testMethodOid={assignItemsPopup.testMethodOid}
          testCode={assignItemsPopup.testCode}
          testName={assignItemsPopup.testName}
          onClose={() => setAssignItemsPopup(null)}
          onSaved={() => {
            setAssignItemsPopup(null);
            void refreshList();
          }}
        />
      )}
      {/* Report Recipient View — per-client, per-kind "View Recipients" popup. */}
      {recipientPopup && (
        <ReportRecipientsDialog
          clientId={recipientPopup.clientId}
          clientName={recipientPopup.clientName}
          kind={recipientPopup.kind}
          kindLabel={recipientPopup.kindLabel}
          onClose={() => setRecipientPopup(null)}
          onSaved={() => {
            setRecipientPopup(null);
            void refreshList();
          }}
        />
      )}
      {/* Instrument picker — two-panel Available/Linked dialog for the
          Instruments Field / Lab / Sample Prep list forms. */}
      {/* Copy Test on a GROUP test: legacy asks WHICH group's members to copy
          before the New form opens (the "Copy Test Group" popup). */}
      {groupCopy && (
        <TestCodeCopyGroupDialog
          onClose={() => setGroupCopy(null)}
          onPick={takeGroupSource}
        />
      )}
      {instrumentPickerPopup && (
        <InstrumentPickerDialog
          testMethodOid={instrumentPickerPopup.testMethodOid}
          testCode={instrumentPickerPopup.testCode}
          testName={instrumentPickerPopup.testName}
          linkTable={instrumentPickerPopup.linkTable}
          onClose={() => setInstrumentPickerPopup(null)}
          onSaved={() => {
            setInstrumentPickerPopup(null);
            void refreshList();
          }}
        />
      )}
    </div>
  );
}

/** Linked-tests popup for the Data Package Review Checklist list view — the
 *  legacy "View Test List" dialog: a Test · Method grid with search + paging.
 *  Parses the row's ALREADY-LOADED `tests` string ("TestName|Method;…") in
 *  memory — no API call, so the data shows instantly with no loading flash. */
function LinkedTestsModal({ tests, onClose }: { tests: string; onClose: () => void }) {
  const rows = useMemo(
    () =>
      tests
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => {
          const i = p.indexOf("|");
          return i >= 0 ? { test: p.slice(0, i).trim(), method: p.slice(i + 1).trim() } : { test: p, method: "" };
        }),
    [tests],
  );
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const term = q.trim().toLowerCase();
  const filtered = term ? rows.filter((r) => `${r.test} ${r.method ?? ""}`.toLowerCase().includes(term)) : rows;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="ltm-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ltm-box" onClick={(e) => e.stopPropagation()}>
        <div className="ltm-head">
          <span>Linked Tests</span>
          <button className="ltm-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="ltm-search">
          <input
            placeholder="Search…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="ltm-grid">
          <table>
            <thead>
              <tr>
                <th>Test</th>
                <th>Method</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="ltm-empty">
                    No data to display
                  </td>
                </tr>
              ) : (
                pageRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.test}</td>
                    <td>{r.method ?? ""}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="ltm-foot">
          <span>
            Page {page} of {totalPages} ({filtered.length} {filtered.length === 1 ? "item" : "items"})
          </span>
          <span className="ltm-pager">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button key={n} className={n === page ? "ltm-pg-active" : ""} onClick={() => setPage(n)}>
                {n}
              </button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              ›
            </button>
          </span>
          <span className="ltm-size">
            Page size:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>

    </div>
  );
}
